import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, Play, Pause, Volume2, VolumeX, Maximize, ExternalLink, ChevronDown } from "lucide-react";
import SiteHeader, { SiteHeaderSpacer } from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { useContactModal } from "@/components/ContactModal";
import XMarkBackground from "@/components/XMarkBackground";
import { findSubmodule } from "@/data/demoModules";
import SubPageNav, { type SubPageNavItem } from "@/components/SubPageNav";
import { supabase } from "@/integrations/supabase/client";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useReveal } from "@/hooks/useReveal";
import { trackEvent } from "@/lib/analytics";
import laptopVideoAsset from "@/assets/LightshowStadium_Final.mp4.asset.json";
const laptopVideoSrc = laptopVideoAsset.url;
import phoneVideoAsset from "@/assets/FanpictorDemo_PhoneColors.mp4.asset.json";
const phoneVideoSrc = phoneVideoAsset.url;
import pulseVideoAsset from "@/assets/Pulse_Video.mp4.asset.json";
const pulseVideoSrc = pulseVideoAsset.url;
import pulseConfiguratorAsset from "@/assets/Pulse_Configurator.mp4.asset.json";
const pulseConfiguratorSrc = pulseConfiguratorAsset.url;
import pulseWinnerAsset from "@/assets/PulseWinner_Final.mp4.asset.json";
const pulseWinnerSrc = pulseWinnerAsset.url;
import pulseConsolationAsset from "@/assets/PulseConsolation_Final.mp4.asset.json";
const pulseConsolationSrc = pulseConsolationAsset.url;
import pulseLoserAsset from "@/assets/PulseLoser_Final.mp4.asset.json";
const pulseLoserSrc = pulseLoserAsset.url;
import mvpVideoAsset from "@/assets/MVP_Video.mp4.asset.json";
const mvpVideoSrc = mvpVideoAsset.url;
import livePredictionVideoAsset from "@/assets/LivePredictions_Video.mp4.asset.json";
const livePredictionVideoSrc = livePredictionVideoAsset.url;
import rookieVideoAsset from "@/assets/RookieoftheSeason_Video.mp4.asset.json";
const rookieVideoSrc = rookieVideoAsset.url;
import coolestScorerVideoAsset from "@/assets/CoolestScorer_Video.mp4.asset.json";
const coolestScorerVideoSrc = coolestScorerVideoAsset.url;
import overallStatisticsVideoAsset from "@/assets/AudienceProfile_OverallStatistics.mp4.asset.json";
const overallStatisticsVideoSrc = overallStatisticsVideoAsset.url;
import fanProfileVideoAsset from "@/assets/AudienceProfile_FanProfile.mp4.asset.json";
const fanProfileVideoSrc = fanProfileVideoAsset.url;
import engagementBehaviorVideoAsset from "@/assets/EngagementBehavior_FeatureStatistics.mp4.asset.json";
const engagementBehaviorVideoSrc = engagementBehaviorVideoAsset.url;
const quiz01 = "/__l5e/assets-v1/ccfbb5e9-c68d-42ea-a6cd-f71f73af19ca/01_QuizStart_Final.mp4";
const quiz02 = "/__l5e/assets-v1/0960a7f7-a401-4cee-9ff0-6bbdce7ee590/02_TextQuiz_Final.mp4";
const quiz03 = "/__l5e/assets-v1/cf1d4417-6ef2-4093-8215-c67103216cce/03_VideoQuiz_Final.mp4";
const quiz04 = "/__l5e/assets-v1/1604887b-1f11-4a67-be10-89cd732b4efb/04_ImageQuiz_Final.mp4";
const quiz05 = "/__l5e/assets-v1/a623df3f-b767-4ca1-8d47-3d60e530b577/05_BlurryImageQuiz_Final.mp4";
const quiz06 = "/__l5e/assets-v1/5b6d1dfb-91b9-4d22-9f53-48666a71e9db/06_ZoomedInQuiz_Final.mp4";
const quiz07 = "/__l5e/assets-v1/66a52c92-f298-4d46-9d82-3fd225ae6e04/07_Spot_SelectQuiz_Final.mp4";
const quiz08 = "/__l5e/assets-v1/2719443e-136e-4764-84e3-44f34657cdfc/08_QuizEnd_Final.mp4";
const poll01 = "";
const poll02 = "";
const poll03 = "";
const poll04 = "";
const poll05 = "";
import brandedUiLogoAsset from "@/assets/branded/BrandedEngagement_UI_Logo.mp4.asset.json";
import brandedMainSponsorAsset from "@/assets/branded/MainSponsorPlacement.mp4.asset.json";
import brandedLeaderboardAsset from "@/assets/branded/LeaderboardSponsor.mp4.asset.json";
import brandedMvpCtaAsset from "@/assets/branded/MVPSponsorCTA.mp4.asset.json";
import brandedQuizPollCtaAsset from "@/assets/branded/Quiz_PollCTA.mp4.asset.json";
import brandedSponsoredGameAsset from "@/assets/branded/Quiz.mp4.asset.json";
import designConfigLoginAsset from "@/assets/configuration/01_LoginDesignConfig.mp4.asset.json";
import designConfigGameAsset from "@/assets/configuration/02_GameConfig.mp4.asset.json";
import designConfigLeaderboardAsset from "@/assets/configuration/03_LeaderboardConfig.mp4.asset.json";
import contentConfigLoginAsset from "@/assets/configuration/01_LoginContentConfig.mp4.asset.json";
import contentConfigGameAsset from "@/assets/configuration/02_GameContentConfig.mp4.asset.json";
import contentConfigLeaderboardAsset from "@/assets/configuration/03_LeaderboardContentConfig.mp4.asset.json";

const pollsDemoSteps: {
  src: string;
  step: string;
  label: string;
  scale?: number;
  objectPosition?: string;
}[] = [
  { src: poll01, step: "01", label: "Start" },
  { src: poll02, step: "02", label: "Choice Poll" },
  { src: poll03, step: "03", label: "Scale Poll" },
  { src: poll04, step: "04", label: "Drag & Drop" },
  { src: poll05, step: "05", label: "Result" },
];

const quizzesDemoSteps: {
  src: string;
  step: string;
  label: string;
  scale?: number;
  objectPosition?: string;
}[] = [
  { src: quiz01, step: "01", label: "Start" },
  { src: quiz02, step: "02", label: "Text Quiz" },
  { src: quiz03, step: "03", label: "Video Quiz" },
  { src: quiz04, step: "04", label: "Image Quiz" },
  { src: quiz05, step: "05", label: "Blurred Image" },
  { src: quiz06, step: "06", label: "Zoomed In" },
  { src: quiz07, step: "07", label: "Spot & Select" },
  { src: quiz08, step: "08", label: "RESULT & CTA" },
];

const audienceEngagementDemoMedia: Record<
  string,
  {
    laptopSrc?: string;
    phoneSrc?: string;
    laptopMuted?: boolean;
    phoneMuted?: boolean;
  }
> = {
  lightshow: {
    laptopSrc: laptopVideoSrc,
    phoneSrc: phoneVideoSrc,
    laptopMuted: false,
    phoneMuted: true,
  },
  pulse: {
    phoneSrc: pulseVideoSrc,
    phoneMuted: true,
  },
  "mvp-voting": {
    phoneSrc: mvpVideoSrc,
    phoneMuted: true,
  },
  "live-prediction": {
    phoneSrc: livePredictionVideoSrc,
    phoneMuted: true,
  },
  "rookie-of-the-season": {
    phoneSrc: rookieVideoSrc,
    phoneMuted: true,
  },
  "coolest-scorer": {
    phoneSrc: coolestScorerVideoSrc,
    phoneMuted: true,
  },
};

const Reveal = ({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  as?: any;
  className?: string;
}) => {
  const { ref, visible } = useReveal<HTMLDivElement>();
  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
      className={`transform-gpu transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none motion-reduce:transform-none ${
        visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0 motion-reduce:opacity-100"
      } ${className}`}
    >
      {children}
    </Tag>
  );
};

const SimpleVideoSection = ({ featureLabel }: { featureLabel: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideo = async () => {
      const { data } = await supabase
        .from("demo_feature_videos")
        .select("video_url")
        .eq("feature_label", featureLabel)
        .maybeSingle();
      if (data?.video_url) setVideoUrl(data.video_url);
    };
    fetchVideo();
  }, [featureLabel]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleFullscreen = () => videoRef.current?.requestFullscreen();

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  return (
    <section className="px-8 pb-16 md:px-16 md:pb-24 lg:px-24 lg:pb-32">
      <div className="mx-auto max-w-6xl">
        <div className="relative aspect-video w-full overflow-hidden bg-foreground/5">
          {videoUrl ? (
            <>
              <video
                ref={videoRef}
                src={videoUrl}
                className="h-full w-full object-contain"
                muted={isMuted}
                playsInline
                onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
                onEnded={() => setIsPlaying(false)}
                onClick={togglePlay}
              />
              {!isPlaying && (
                <button onClick={togglePlay} aria-label="Play video" className="absolute inset-0 flex items-center justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-foreground/30 backdrop-blur-sm transition-colors hover:bg-foreground/50">
                    <Play className="h-7 w-7 text-foreground fill-foreground" />
                  </div>
                </button>
              )}
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <p className="font-body text-sm text-muted-foreground">No video uploaded</p>
            </div>
          )}
        </div>

        {videoUrl && (
          <div className="mt-4 flex items-center gap-4">
            <button onClick={togglePlay} aria-label={isPlaying ? "Pause video" : "Play video"} className="text-foreground transition-colors hover:text-muted-foreground">
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <span className="font-body text-xs text-muted-foreground tabular-nums">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button onClick={toggleMute} aria-label={isMuted ? "Unmute video" : "Mute video"} className="text-foreground transition-colors hover:text-muted-foreground">
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
            <input
              type="range"
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              aria-label="Video progress"
              className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-foreground/20 accent-foreground [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground"
            />
            <button onClick={handleFullscreen} aria-label="Enter fullscreen" className="text-foreground transition-colors hover:text-muted-foreground">
              <Maximize className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const VideoMockupSection = ({ featureLabel }: { featureLabel: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideo = async () => {
      const { data } = await supabase
        .from("demo_feature_videos")
        .select("video_url")
        .eq("feature_label", featureLabel)
        .maybeSingle();
      if (data?.video_url) setVideoUrl(data.video_url);
    };
    fetchVideo();
  }, [featureLabel]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleFullscreen = () => {
    videoRef.current?.requestFullscreen();
  };

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  return (
    <section className="px-8 pb-16 md:px-16 md:pb-24 lg:px-24 lg:pb-32">
      <div className="mx-auto max-w-6xl">
        {/* Device mockups */}
        <div className="flex items-end gap-6 md:gap-10">
          {/* Laptop */}
          <div className="flex-1">
            <div className="rounded-t-xl border-[3px] border-b-0 border-foreground/20 bg-foreground/10 p-1.5 md:p-2">
              <div className="relative aspect-video w-full overflow-hidden rounded-t-lg bg-background">
                {videoUrl ? (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    className="h-full w-full object-contain"
                    muted={isMuted}
                    playsInline
                    onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                    onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
                    onEnded={() => setIsPlaying(false)}
                    onClick={togglePlay}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <p className="font-body text-sm text-muted-foreground">No video uploaded</p>
                  </div>
                )}
                {videoUrl && !isPlaying && (
                  <button
                    onClick={togglePlay}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-foreground/30 backdrop-blur-sm transition-colors hover:bg-foreground/50">
                      <Play className="h-7 w-7 text-foreground fill-foreground" />
                    </div>
                  </button>
                )}
              </div>
            </div>
            {/* Laptop base */}
            <div className="mx-auto h-3 w-[110%] -translate-x-[5%] rounded-b-lg bg-gradient-to-b from-foreground/15 to-foreground/10" />
            <div className="mx-auto h-1.5 w-[30%] rounded-b-md bg-foreground/10" />
          </div>

          {/* Phone */}
          <div className="hidden w-[18%] shrink-0 md:block">
            <div className="rounded-[1.2rem] border-[3px] border-foreground/20 bg-foreground/10 p-1.5">
              {/* Notch */}
              <div className="relative overflow-hidden rounded-[0.9rem] bg-background">
                <div className="mx-auto mt-1.5 h-4 w-[35%] rounded-full bg-foreground/10" />
                <div className="aspect-[9/19] w-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Video controls */}
        {videoUrl && (
          <div className="mt-6 flex items-center gap-4">
            <button onClick={togglePlay} aria-label={isPlaying ? "Pause video" : "Play video"} className="text-foreground transition-colors hover:text-muted-foreground">
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <span className="font-body text-xs text-muted-foreground tabular-nums">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button onClick={toggleMute} aria-label={isMuted ? "Unmute video" : "Mute video"} className="text-foreground transition-colors hover:text-muted-foreground">
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
            <input
              type="range"
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              aria-label="Video progress"
              className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-foreground/20 accent-foreground [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground"
            />
            <button onClick={handleFullscreen} aria-label="Enter fullscreen" className="text-foreground transition-colors hover:text-muted-foreground">
              <Maximize className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const AudienceProfileSection = () => {
  const sections: {
    eyebrow: string;
    headline: string;
    body: string[];
    bullets: string[];
    closing: string;
    videoSrc: string;
    videoOnLeft: boolean;
  }[] = [
    {
      eyebrow: "AUDIENCE PROFILE",
      headline: "Understand your audience at scale",
      body: [
        "Audience Profile gives organisations a structured overview of who their users are, where they come from, and how their audience base develops over time.",
        "The dashboard brings together key audience statistics, including registered users, confirmed registrations, active users, country distribution, monthly registrations, registration growth, confirmed users by date, age groups, gender distribution, and participation trends across the platform.",
        "This allows teams to move beyond surface-level engagement numbers and understand the actual composition and development of their audience.",
      ],
      bullets: [
        "Registered and confirmed users",
        "Active user development",
        "Country and geographic distribution",
        "Monthly and historical registration trends",
        "Age and gender breakdowns",
        "Participation trends across features",
      ],
      closing: "Audience data becomes easier to read, compare, and act on.",
      videoSrc: overallStatisticsVideoSrc,
      videoOnLeft: true,
    },
    {
      eyebrow: "FAN PROFILE",
      headline: "Move from audience segments to individual profiles",
      body: [
        "The Fan Profile view gives teams a deeper look into individual audience members and their interaction history across Fanpictor experiences.",
        "Each profile can include personal and behavioral information such as name, gender, date of birth, age, country, language, email, registration date, confirmation date, and last login.",
        "The activity view shows how a user has interacted over time, including which features they participated in, when they participated, how many points they earned, and how their engagement developed across the platform.",
        "More detailed profile insights can include monthly participation breakdowns, feature-specific activity, average engagement rate, ranking, total points, and engagement growth.",
      ],
      bullets: [
        "Individual audience profile details",
        "Registration and confirmation history",
        "Last login and activity timeline",
        "Feature participation history",
        "Points, ranking, and engagement growth",
        "Monthly activity and feature breakdowns",
      ],
      closing: "Fan profiles turn participation data into a clearer understanding of each user.",
      videoSrc: fanProfileVideoSrc,
      videoOnLeft: false,
    },
  ];

  return (
    <section data-demo-section className="relative overflow-hidden px-5 pt-36 pb-20 md:px-16 md:py-28 lg:px-24 lg:py-32">

      <div className="relative z-10 mx-auto flex max-w-8xl flex-col gap-24 md:gap-32">
        {sections.map((s, idx) => {
          const VideoBlock = (
            <Reveal delay={idx === 0 ? 120 : 0} className="w-full">
              <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-neutral-50 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]">
                <video
                  src={s.videoSrc}
                  className="h-full w-full object-contain"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            </Reveal>
          );

          const TextBlock = (
            <div className="w-full">
              <Reveal>
                <div className="h-px w-12 bg-neutral-900/30" />
                <p className="mt-5 font-body text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
                  {s.eyebrow}
                </p>
              </Reveal>
              <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-neutral-900 md:text-4xl lg:text-5xl">
                {s.headline}
              </Reveal>
              <Reveal delay={160} className="mt-6 space-y-4">
                {s.body.map((p, i) => (
                  <p key={i} className="font-body text-sm leading-relaxed text-neutral-700 md:text-base">
                    {p}
                  </p>
                ))}
              </Reveal>
              <Reveal delay={240} className="mt-8">
                <div className="h-px w-full bg-neutral-900/10" />
                <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 font-body text-sm text-neutral-800">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-500" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={320} className="mt-8">
                <div className="h-px w-full bg-neutral-900/10" />
                <p className="mt-6 font-display text-lg font-semibold tracking-tight text-neutral-900 md:text-xl">
                  {s.closing}
                </p>
              </Reveal>
            </div>
          );

          return (
            <div
              key={idx}
              className={`grid grid-cols-1 items-center gap-10 lg:gap-12 ${idx === 1 ? 'lg:grid-cols-[10fr_11fr]' : 'lg:grid-cols-[11fr_10fr]'}`}
            >
              <div className={s.videoOnLeft ? "lg:order-1" : "lg:order-2"}>{VideoBlock}</div>
              <div className={s.videoOnLeft ? "lg:order-2" : "lg:order-1"}>{TextBlock}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const EngagementBehaviorSection = () => {
  const eyebrow = "ENGAGEMENT BEHAVIOR";
  const headline = "Understand how each feature performs";
  const body = [
    "Engagement Behavior gives teams a detailed view of how audiences interact with each Fanpictor feature.",
    "For every gamification module, teams can review how often the feature was used, how many events included it, total participations, average participation per event, and the percentage of users who engaged.",
    "The view also makes it possible to see which users participated, with direct access to their individual fan profiles for deeper context.",
    "This helps organisations understand which features drive the strongest activity, where audience interest is highest, and how engagement behavior can support more relevant marketing and sponsorship activation.",
    "For example, if fans repeatedly vote for a specific player in MVP Voting, that behavior can become the basis for a targeted campaign, such as a merchandise offer or sponsor activation connected to that player.",
  ];
  const bullets = [
    "Feature-specific participation data",
    "Number of events using each feature",
    "Total and average participation",
    "Engagement percentage by users",
    "Participating users with profile access",
    "Activation opportunities based on behavior",
  ];
  const closing = "Feature engagement data turns participation into actionable audience intelligence.";

  return (
    <section data-demo-section className="relative overflow-hidden px-5 pt-36 pb-20 md:px-16 md:py-28 lg:px-24 lg:py-32">

      <div className="relative z-10 mx-auto max-w-8xl">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[11fr_10fr] lg:gap-12">
          <Reveal delay={120} className="w-full">
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-neutral-50 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]">
              <video
                src={engagementBehaviorVideoSrc}
                className="h-full w-full object-contain"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          </Reveal>

          <div className="w-full">
            <Reveal>
              <div className="h-px w-12 bg-neutral-900/30" />
              <p className="mt-5 font-body text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
                {eyebrow}
              </p>
            </Reveal>
            <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-neutral-900 md:text-4xl lg:text-5xl">
              {headline}
            </Reveal>
            <Reveal delay={160} className="mt-6 space-y-4">
              {body.map((p, i) => (
                <p key={i} className="font-body text-sm leading-relaxed text-neutral-700 md:text-base">
                  {p}
                </p>
              ))}
            </Reveal>
            <Reveal delay={240} className="mt-8">
              <div className="h-px w-full bg-neutral-900/10" />
              <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 font-body text-sm text-neutral-800">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={320} className="mt-8">
              <div className="h-px w-full bg-neutral-900/10" />
              <p className="mt-6 font-display text-lg font-semibold tracking-tight text-neutral-900 md:text-xl">
                {closing}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const LightshowDemoSection = ({
  title = "Lightshow",
  heading,
  description,
  moduleSlug,
  submoduleSlug,
}: {
  title?: string;
  heading?: string;
  description?: string;
  moduleSlug?: string;
  submoduleSlug?: string;
}) => {
  const isAudienceEngagement = moduleSlug === "audience-engagement";
  const normalizedSlug = submoduleSlug?.trim().toLowerCase();
  const media = (normalizedSlug && audienceEngagementDemoMedia[normalizedSlug]) || null;
  const laptopVideo = media?.laptopSrc;
  const phoneVideo = media?.phoneSrc;
  const hasLaptopVideo = Boolean(laptopVideo);
  const hasPhoneVideo = Boolean(phoneVideo);
  const isPulse = normalizedSlug === "pulse";
  const isMvp = normalizedSlug === "mvp-voting";
  const isLivePrediction = normalizedSlug === "live-prediction";
  const isRookie = normalizedSlug === "rookie-of-the-season";
  const isCoolestScorer = normalizedSlug === "coolest-scorer";
  const isLightshow = normalizedSlug === "lightshow";
  const isSplitLayout = isMvp || isLivePrediction || isRookie || isCoolestScorer;
  if (typeof window !== "undefined") {
    // Temporary debug
    console.log("[DemoFeature]", { moduleSlug, submoduleSlug, normalizedSlug, hasLaptopVideo, hasPhoneVideo, laptopVideo, phoneVideo });
  }
  const laptopRef = useRef<HTMLVideoElement>(null);
  const phoneRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const syncPhoneWithLaptop = () => {
    const l = laptopRef.current;
    const p = phoneRef.current;
    if (!l || !p || !hasPhoneVideo) return;

    const drift = Math.abs(p.currentTime - l.currentTime);
    if (drift > 0.35) {
      p.currentTime = l.currentTime;
    }
  };

  const handlePlayToggle = async () => {
    const l = laptopRef.current;
    const p = phoneRef.current;

    if (!l || !hasLaptopVideo) return;

    if (!l.paused && !l.ended) {
      l.pause();
      p?.pause();
      setIsPlaying(false);
      return;
    }

    l.muted = media?.laptopMuted ?? true;

    const playAttempts: Promise<unknown>[] = [l.play()];

    if (p && hasPhoneVideo) {
      p.muted = media?.phoneMuted ?? true;
      p.currentTime = l.currentTime;
      playAttempts.push(p.play());
    }

    const [laptopResult] = await Promise.allSettled(playAttempts);

    if (laptopResult?.status === "fulfilled") {
      setIsPlaying(true);
      return;
    }

    p?.pause();
    setIsPlaying(false);
  };

  return (
    <section
      data-demo-section
      className="relative flex min-h-screen flex-col justify-center px-5 pt-36 pb-20 md:px-16 md:py-24 lg:px-24"
    >

      <div className="relative z-10 mb-12 max-w-4xl md:mb-16">
        <Reveal as="h3" className="font-display text-3xl font-black tracking-tight text-neutral-900 md:text-5xl lg:text-6xl">
          {heading ?? (isLightshow ? "Built for every audience scale" : `See ${title} in action`)}
        </Reveal>
        <Reveal delay={120} as="p" className="mt-6 max-w-3xl font-body text-sm leading-relaxed text-neutral-600 md:text-base lg:text-lg">
          {description ?? (isLightshow ? "Whether the audience is 100 people in a conference venue or tens of thousands inside a stadium, Lightshow adapts to the environment." : isPulse ? "Pulse can run through an existing app or as a standalone deployment." : "Launch the experience on venue screens while audience phones join through a QR code or app entry point.")}
        </Reveal>
      </div>

      <div className={`relative z-10 mx-auto w-full ${isPulse ? "max-w-[88rem]" : "max-w-6xl"}`}>
        {isSplitLayout ? (
          <div className="grid grid-cols-1 items-center gap-12 md:gap-16 lg:grid-cols-2 lg:gap-20">
            {/* Video Preview */}
            <Reveal delay={240} className="mx-auto w-full max-w-[360px] lg:mx-0">
              <div
                className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-black shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                style={{ aspectRatio: "1054 / 1875" }}
              >
                {hasPhoneVideo ? (
                  <video
                    ref={phoneRef}
                    src={phoneVideo}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ transform: "scale(1.18)", transformOrigin: "center center" }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-4">
                    <p className="text-center font-body text-[10px] uppercase leading-relaxed tracking-[0.18em] text-white/45">
                      Demo video coming soon
                    </p>
                  </div>
                )}
              </div>
            </Reveal>

            {/* Content Panel */}
            {isMvp ? (
              <div className="mx-auto w-full max-w-[480px] lg:mx-0">
                <Reveal delay={300} as="p" className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  MVP Voting
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-4 h-px w-12 bg-neutral-900/30" />
                </Reveal>
                <Reveal delay={420} as="h4" className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
                  Built for sports, entertainment, and beyond
                </Reveal>
                <Reveal delay={480} as="p" className="mt-6 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  MVP Voting helps audiences recognize standout performers in real time. In sports, it can be used to vote for the Most Valuable Player, highlight key moments, or drive fan participation around matches and tournaments.
                </Reveal>
                <Reveal delay={540} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Beyond sports, the same format can be adapted for entertainment experiences — from selecting the most valuable performer, artist, or act, to powering audience-led recognition moments during shows, concerts, and live events.
                </Reveal>
                <Reveal delay={600} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  It is fully customizable in both logic and presentation, allowing Fanpictor to tailor the experience to different event formats, audience journeys, and engagement goals.
                </Reveal>
                <Reveal delay={660}>
                  <div className="mt-8 h-px w-full bg-neutral-900/10" />
                </Reveal>
                <Reveal delay={720} as="p" className="mt-6 font-body text-xs uppercase tracking-[0.18em] text-neutral-500">
                  From MVP to MVA — the structure stays flexible.
                </Reveal>
              </div>
            ) : isLivePrediction ? (
              <div className="mx-auto w-full max-w-[560px] lg:mx-0">
                <Reveal delay={300} as="p" className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  Live Prediction
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-4 h-px w-12 bg-neutral-900/30" />
                </Reveal>
                <Reveal delay={420} as="h4" className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
                  Built for sports, entertainment, and live experiences
                </Reveal>
                <Reveal delay={480} as="p" className="mt-6 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Live Prediction lets audiences anticipate what happens next, turning passive viewing into active participation.
                </Reveal>
                <Reveal delay={540} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  In sports, it can be used for score predictions, the next free kick, the next alley-oop, the next scorer, the next race result, or any other key moment within the event flow.
                </Reveal>
                <Reveal delay={600} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Beyond sports, the same mechanic can be tailored to entertainment experiences — such as predicting the opening song of an artist, the next performer, the next stage moment, or other audience-relevant outcomes during concerts, festivals, and live shows.
                </Reveal>
                <Reveal delay={660} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Because the format is modular and highly customizable, Fanpictor can adapt Live Prediction to different event types, audience behaviors, and participation goals.
                </Reveal>

                <Reveal delay={720}>
                  <div className="mt-8 h-px w-full bg-neutral-900/10" />
                </Reveal>
                <Reveal delay={780} as="p" className="mt-6 font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  Prediction status logic
                </Reveal>
                <Reveal delay={840}>
                  <ul className="mt-4 space-y-2.5 font-body text-sm text-neutral-700">
                    <li className="flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-600/80" />
                      <span><span className="font-medium text-emerald-700/90">Green</span> <span className="text-neutral-500">— correct prediction</span></span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-rose-600/80" />
                      <span><span className="font-medium text-rose-700/90">Red</span> <span className="text-neutral-500">— incorrect prediction</span></span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500/80" />
                      <span><span className="font-medium text-amber-700/90">Yellow</span> <span className="text-neutral-500">— pending result</span></span>
                    </li>
                  </ul>
                </Reveal>
                <Reveal delay={900} as="p" className="mt-4 font-body text-[13px] leading-relaxed text-neutral-500">
                  Pending predictions remain open until the relevant action happens, the result is confirmed, or the event phase ends.
                </Reveal>

                <Reveal delay={960}>
                  <div className="mt-8 h-px w-full bg-neutral-900/10" />
                </Reveal>
                <Reveal delay={1020} as="p" className="mt-6 font-body text-xs uppercase tracking-[0.18em] text-neutral-500">
                  From scorelines to show moments — prediction logic adapts to the experience.
                </Reveal>
              </div>
            ) : isRookie ? (
              <div className="mx-auto w-full max-w-[480px] lg:mx-0">
                <Reveal delay={300} as="p" className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  Rookie of the Season
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-4 h-px w-12 bg-neutral-900/30" />
                </Reveal>
                <Reveal delay={420} as="h4" className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
                  Season-long recognition built around emerging talent
                </Reveal>
                <Reveal delay={480} as="p" className="mt-6 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Rookie of the Season turns recognition into an ongoing audience engagement format. Fans can vote for standout newcomers, rising players, or emerging performers across a season, tournament, campaign, or event series.
                </Reveal>
                <Reveal delay={540} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  For sports clubs and federations, it can highlight young talent, increase fan involvement, and create recurring participation moments beyond individual match days.
                </Reveal>
                <Reveal delay={600} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  The same structure can also be adapted beyond sports, from recognizing new performers and artists to spotlighting fresh talent in entertainment, cultural, or branded event formats.
                </Reveal>
                <Reveal delay={660} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Because the format is customizable, Fanpictor can adapt the voting logic, nomination structure, timing, branding, and sponsor integration around the specific experience.
                </Reveal>
                <Reveal delay={720}>
                  <div className="mt-8 h-px w-full bg-neutral-900/10" />
                </Reveal>
                <Reveal delay={780} as="p" className="mt-6 font-body text-xs uppercase tracking-[0.18em] text-neutral-500">
                  From rising players to emerging performers — recognition becomes a repeatable engagement moment.
                </Reveal>
              </div>
            ) : (
              <div className="mx-auto w-full max-w-[480px] lg:mx-0">
                <Reveal delay={300} as="p" className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  Coolest Scorer
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-4 h-px w-12 bg-neutral-900/30" />
                </Reveal>
                <Reveal delay={420} as="h4" className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
                  A voting format built around standout moments
                </Reveal>
                <Reveal delay={480} as="p" className="mt-6 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Coolest Scorer lets audiences vote for the most memorable scoring moment of a game, match, show, or event. In sports, it can highlight the goal, point, basket, or play that fans felt was the most exciting, creative, or impactful.
                </Reveal>
                <Reveal delay={540} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  The mechanic can be adapted beyond sports as well. For entertainment and live events, it can be used to vote for the best performance moment, most impressive act, strongest stage highlight, or any audience-defined moment worth recognizing.
                </Reveal>
                <Reveal delay={600} as="p" className="mt-4 font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Because the format is flexible, Fanpictor can tailor the voting logic, naming, visuals, sponsor integration, and result structure around the specific event or campaign.
                </Reveal>
                <Reveal delay={720}>
                  <div className="mt-8 h-px w-full bg-neutral-900/10" />
                </Reveal>
                <Reveal delay={780} as="p" className="mt-6 font-body text-xs uppercase tracking-[0.18em] text-neutral-500">
                  From goals to show highlights — memorable moments become measurable engagement.
                </Reveal>
              </div>
            )}
          </div>
        ) : isPulse ? (
        <div className="flex flex-col gap-24 md:gap-36 lg:gap-44">
          {/* Row 1: Landscape configuration video + Pulse Editor text */}
          <Reveal delay={240} className="flex flex-col items-center gap-10 md:flex-row md:items-center md:gap-16 lg:gap-20">
            <div className="w-full md:w-[64%] md:shrink-0 md:-ml-6 lg:-ml-10">
              <div
                className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-black shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                style={{ aspectRatio: "16 / 9" }}
              >
                <video
                  src={pulseConfiguratorSrc}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ transform: "scaleX(1.12) scaleY(1.06)", transformOrigin: "center center", objectPosition: "center" }}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  tabIndex={-1}
                />
              </div>
            </div>

            <div className="w-full md:w-[36%] md:shrink-0">
              <p className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                PULSE EDITOR
              </p>
              <div className="mt-4 h-px w-12 bg-neutral-900/30" />
              <h4 className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
                Build Pulse experiences around your brand and event logic
              </h4>
              <div className="mt-6 space-y-4">
                <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  The Pulse Editor gives teams full control over how a Pulse activation is configured, branded, timed, and rewarded.
                </p>
                <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  Organizations can define custom pulse colors based on their brand guidelines, choose how long each color appears on screen, set the total Pulse duration, and decide whether the experience should run as a synchronized or unsynchronized activation.
                </p>
                <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  A synchronized Pulse brings participants into the same live moment at a defined time. An unsynchronized Pulse allows audiences to join more flexibly, without every participant needing to enter at the exact same moment.
                </p>
                <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                  The setup can also include sponsor placements, favorite or dominant color behavior, winner prizes, and consolation prize logic, making Pulse adaptable for different event formats, campaign goals, and commercial setups.
                </p>
              </div>
              <ul className="mt-8 space-y-2.5">
                {[
                  "Brand-based color configuration",
                  "Start and end date control",
                  "Custom pulse timing and duration",
                  "Synchronized and unsynchronized modes",
                  "Sponsor placement options",
                  "Winner and consolation prize setup",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 font-body text-sm leading-relaxed text-neutral-700">
                    <span aria-hidden className="mt-2 inline-block h-px w-4 shrink-0 bg-neutral-900/40" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 h-px w-full bg-neutral-900/10" />
              <p className="mt-6 font-display text-base font-semibold tracking-tight text-neutral-900 md:text-lg">
                Pulse can be tailored to the moment, the audience, and the activation goal.
              </p>
            </div>
          </Reveal>

          {/* Row 2: Three vertical Pulse outcome videos */}
          <Reveal delay={320}>
            <PulseOutcomesMobileCarousel className="md:hidden" />
            <div className="hidden md:grid grid-cols-1 gap-12 sm:grid-cols-3 md:gap-14 lg:gap-20">
              {[
                { src: pulseWinnerSrc, label: "Pulse Prize Winner" },
                { src: pulseConsolationSrc, label: "Pulse Consolation Prize Winner" },
                { src: pulseLoserSrc, label: "Pulse Non-Winning Outcome" },
              ].map((item) => (
                <div key={item.label} className="flex flex-col items-center">
                  <div className="w-full max-w-[300px]">
                    <div
                      className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-black shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                      style={{ aspectRatio: "975 / 1874" }}
                    >
                      <video
                        src={item.src}
                        className="absolute inset-0 h-full w-full object-cover"
                        style={{ transform: "scale(1.13)", transformOrigin: "center center" }}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        tabIndex={-1}
                      />
                    </div>
                  </div>
                  <p className="mt-6 font-body text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>

        </div>

        ) : (
        <Reveal delay={240} className="flex flex-col items-center gap-10 md:flex-row md:gap-20 lg:gap-24">
          {/* Laptop */}
          <div className="w-full md:w-[68%] md:shrink-0 md:-ml-4 lg:-ml-8">
            <div className="rounded-t-xl border-[3px] border-b-0 border-neutral-900/80 bg-neutral-900 p-1.5 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.35)] md:p-2">
              <div className="relative aspect-video w-full overflow-hidden rounded-t-lg bg-black">
                {hasLaptopVideo ? (
                  <>
                    <video
                      ref={laptopRef}
                      src={laptopVideo}
                      className="h-full w-full object-contain"
                      playsInline
                      preload="metadata"
                      onClick={handlePlayToggle}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => {
                        setIsPlaying(false);
                        if (phoneRef.current && !phoneRef.current.paused) {
                          phoneRef.current.pause();
                        }
                      }}
                      onSeeking={syncPhoneWithLaptop}
                      onTimeUpdate={syncPhoneWithLaptop}
                      onEnded={() => {
                        setIsPlaying(false);
                        if (phoneRef.current) {
                          phoneRef.current.pause();
                          phoneRef.current.currentTime = 0;
                        }
                      }}
                    />
                    {!isPlaying ? (
                      <button
                        type="button"
                        onClick={handlePlayToggle}
                        className="absolute inset-0 z-10 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
                        aria-label="Play demo"
                      >
                        <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur-sm">
                          <Play className="h-6 w-6 fill-white text-white" />
                        </div>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handlePlayToggle}
                        className="absolute inset-0 z-10"
                        aria-label="Pause demo"
                      />
                    )}
                  </>
                ) : isAudienceEngagement ? (
                  <div className="flex h-full w-full items-center justify-center px-6">
                    <p className="text-center font-body text-xs uppercase tracking-[0.2em] text-white/60">
                      Demo video coming soon
                    </p>
                  </div>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur-sm">
                        <Play className="h-5 w-5 text-white/80" />
                      </div>
                      <p className="font-body text-xs uppercase tracking-[0.2em] text-white/60">
                        {title} demo video
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mx-auto h-3 w-[110%] -translate-x-[5%] rounded-b-lg bg-gradient-to-b from-neutral-700 to-neutral-500" />
            <div className="mx-auto h-1.5 w-[30%] rounded-b-md bg-neutral-400" />
          </div>

          {/* Phone / Vertical preview */}
          {isPulse ? (
            <div className="hidden w-[28%] shrink-0 md:ml-auto md:block lg:w-[26%]">
              <div
                className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-black shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                style={{ aspectRatio: "975 / 1874" }}
              >
                {hasPhoneVideo ? (
                  <video
                    ref={phoneRef}
                    src={phoneVideo}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ transform: "scale(1.067)", transformOrigin: "center center" }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-4">
                    <p className="text-center font-body text-[10px] uppercase leading-relaxed tracking-[0.18em] text-white/45">
                      Demo video coming soon
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mx-auto w-[55%] max-w-[220px] shrink-0 md:mx-0 md:ml-auto md:block md:w-[16%] md:max-w-none lg:w-[15%]">
              <div className="rounded-[1.4rem] border-[3px] border-neutral-900/80 bg-neutral-900 p-1.5 shadow-[0_20px_45px_-20px_rgba(0,0,0,0.4)]">
                <div className="relative overflow-hidden rounded-[1rem] bg-black">
                  {hasPhoneVideo ? (
                    <div className="relative aspect-[9/19] w-full overflow-hidden">
                      <div className="absolute left-1/2 top-1.5 z-10 h-4 w-[35%] -translate-x-1/2 rounded-full bg-black/60" />
                      <video
                        ref={phoneRef}
                        src={phoneVideo}
                        className="absolute inset-0 h-full w-full object-cover"
                        muted={media?.phoneMuted ?? true}
                        playsInline
                        preload="metadata"
                        tabIndex={-1}
                      />
                    </div>
                  ) : isAudienceEngagement ? (
                    <div className="relative aspect-[9/19] w-full">
                      <div className="mx-auto mt-1.5 h-4 w-[35%] rounded-full bg-white/15" />
                      <div className="flex h-full w-full items-center justify-center px-3 pb-4 pt-2">
                        <p className="text-center font-body text-[10px] uppercase leading-relaxed tracking-[0.18em] text-white/45">
                          Demo video coming soon
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mx-auto mt-1.5 h-4 w-[35%] rounded-full bg-white/15" />
                      <div className="flex aspect-[9/19] w-full items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-white/40" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </Reveal>
        )}
      </div>
    </section>
  );
};

type VideoStep = {
  src: string;
  step: string;
  label: string;
  scale?: number;
  objectPosition?: string;
};

const VideoStepsMobileCarousel = ({
  className,
  steps,
  defaultScale = 1.067,
  videoBgClassName = "bg-white",
  itemLabel = "video",
}: {
  className?: string;
  steps: VideoStep[];
  defaultScale?: number;
  videoBgClassName?: string;
  itemLabel?: string;
}) => {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [index, setIndex] = useState(0);
  const total = steps.length;

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setIndex(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <div className={className}>
      <Carousel opts={{ align: "center", loop: false }} setApi={setApi}>
        <CarouselContent>
          {steps.map((item) => (
            <CarouselItem key={item.src} className="flex flex-col items-center">
              <div className="w-full max-w-[380px]">
                <div
                  className={`relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 ${videoBgClassName} shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]`}
                  style={{ aspectRatio: "1054 / 1875" }}
                >
                  <video
                    src={item.src}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{
                      transform: `scale(${item.scale ?? defaultScale})`,
                      transformOrigin: "center center",
                      objectPosition: item.objectPosition ?? "center",
                    }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                </div>
              </div>
              <p className="mt-5 font-body text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                <span className="text-neutral-900">{item.step}</span>
                <span className="mx-2 text-neutral-300">/</span>
                {item.label}
              </p>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="mt-6 flex items-center justify-center gap-4">
        <span className="font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500">
          <span className="text-neutral-900">{String(index + 1).padStart(2, "0")}</span>
          <span className="mx-2 text-neutral-300">/</span>
          {String(total).padStart(2, "0")}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-center gap-1.5">
        {steps.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to ${itemLabel} ${i + 1}`}
            onClick={() => api?.scrollTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-5 bg-neutral-900" : "w-1.5 bg-neutral-900/25"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const QuizzesMobileCarousel = ({ className }: { className?: string }) => (
  <VideoStepsMobileCarousel
    className={className}
    steps={quizzesDemoSteps}
    defaultScale={1.067}
    videoBgClassName="bg-white"
    itemLabel="quiz"
  />
);

const QuizzesGuidedGrid = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === activeIndex) {
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [activeIndex]);

  return (
    <div className="hidden lg:block">
      <p className="mb-8 font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500 md:mb-10">
        Hover over a quiz step to preview the interaction.
      </p>
      <div className="grid grid-cols-1 gap-x-10 gap-y-16 sm:grid-cols-2 md:gap-x-12 md:gap-y-20 lg:grid-cols-3 lg:gap-x-14">
        {quizzesDemoSteps.map((item, i) => {
          const isActive = i === activeIndex;
          return (
            <Reveal key={item.src} delay={240 + i * 90} className="flex flex-col items-center">
              <div
                onMouseEnter={() => setActiveIndex(i)}
                onFocus={() => setActiveIndex(i)}
                tabIndex={0}
                role="button"
                aria-label={`Preview quiz ${item.step} ${item.label}`}
                className="group w-full max-w-[380px] text-left focus:outline-none cursor-pointer"
              >
                <div
                  className={`relative w-full overflow-hidden rounded-2xl border bg-white transition-all duration-300 ${
                    isActive
                      ? "border-neutral-900/40 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.45)] ring-1 ring-neutral-900/20"
                      : "border-neutral-900/10 shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                  }`}
                  style={{ aspectRatio: "1054 / 1875" }}
                >
                  <video
                    ref={(el) => (videoRefs.current[i] = el)}
                    src={item.src}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{
                      transform: `scale(${item.scale ?? 1.067})`,
                      transformOrigin: "center center",
                      objectPosition: item.objectPosition ?? "center",
                    }}
                    autoPlay={isActive}
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                  {!isActive && (
                    <div
                      className="pointer-events-none absolute inset-0 flex items-center justify-center"
                      style={{ backgroundColor: "#000000", opacity: 1 }}
                    >
                      <span className="font-body text-[11px] uppercase tracking-[0.2em] text-white">
                        Hover Your Mouse Here
                      </span>
                    </div>
                  )}
                </div>
                <p
                  className={`mt-5 font-body text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    isActive ? "text-neutral-900" : "text-neutral-500"
                  }`}
                >
                  <span className={isActive ? "font-semibold text-neutral-900" : "text-neutral-900"}>
                    {item.step}
                  </span>
                  <span className="mx-2 text-neutral-300">/</span>
                  {item.label}
                </p>
              </div>
            </Reveal>
          );
        })}

        <Reveal
          delay={300}
          className="sm:col-span-2 lg:col-span-1 lg:col-start-3 lg:row-start-3 lg:self-center"
        >
          <div className="mx-auto w-full max-w-[420px] lg:mx-0">
            <Reveal delay={360}>
              <div className="h-px w-12 bg-neutral-900/30" />
            </Reveal>
            <p className="mt-5 font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500">
              Quiz Formats
            </p>
            <h4 className="mt-4 font-display text-2xl font-black leading-[1.1] tracking-tight text-neutral-900 md:text-3xl">
              Built-in quiz types.
              <br />
              Flexible by design.
            </h4>
            <Reveal delay={420}>
              <div className="mt-6 h-px w-full bg-neutral-900/10" />
            </Reveal>
            <div className="mt-6 space-y-4 font-body text-sm leading-relaxed text-neutral-600">
              <p>
                Fanpictor already includes a range of quiz formats designed to support different
                types of audience interaction, from quick knowledge checks to more visual and
                interactive challenges.
              </p>
              <p>
                All existing quiz types can be easily customized to match the event, campaign,
                sponsor, or brand experience.
              </p>
            </div>
            <Reveal delay={480}>
              <div className="mt-7 h-px w-full bg-neutral-900/10" />
            </Reveal>
            <ul className="mt-5 grid grid-cols-1 gap-y-2 font-body text-[13px] text-neutral-800">
              {[
                "Text Quiz",
                "Video Quiz",
                "Image Quiz",
                "Blurred Image Quiz",
                "Zoomed-In Quiz",
                "Spot & Select Quiz",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="h-px w-4 bg-neutral-900/40" />
                  {t}
                </li>
              ))}
            </ul>
            <Reveal delay={540}>
              <div className="mt-7 h-px w-full bg-neutral-900/10" />
            </Reveal>
            <p className="mt-5 font-body text-[13px] leading-relaxed text-neutral-500">
              Need something more specific? Custom quiz mechanics can be developed on request.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

const PollsMobileCarousel = ({ className }: { className?: string }) => (
  <VideoStepsMobileCarousel
    className={className}
    steps={pollsDemoSteps}
    defaultScale={1.19}
    videoBgClassName="bg-black"
    itemLabel="poll"
  />
);

const PulseOutcomesMobileCarousel = ({ className }: { className?: string }) => {
  const items = [
    { src: pulseWinnerSrc, label: "Pulse Prize Winner" },
    { src: pulseConsolationSrc, label: "Pulse Consolation Prize Winner" },
    { src: pulseLoserSrc, label: "Pulse Non-Winning Outcome" },
  ];
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [index, setIndex] = useState(0);
  const total = items.length;

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setIndex(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <div className={className}>
      <Carousel opts={{ align: "center", loop: false }} setApi={setApi}>
        <CarouselContent>
          {items.map((item) => (
            <CarouselItem key={item.label} className="flex flex-col items-center">
              <div className="w-full max-w-[300px]">
                <div
                  className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-black shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                  style={{ aspectRatio: "975 / 1874" }}
                >
                  <video
                    src={item.src}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ transform: "scale(1.13)", transformOrigin: "center center" }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                </div>
              </div>
              <p className="mt-6 font-body text-[11px] uppercase tracking-[0.2em] text-neutral-500 text-center">
                {item.label}
              </p>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="mt-6 flex items-center justify-center gap-4">
        <span className="font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500">
          <span className="text-neutral-900">{String(index + 1).padStart(2, "0")}</span>
          <span className="mx-2 text-neutral-300">/</span>
          {String(total).padStart(2, "0")}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-center gap-1.5">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to pulse outcome ${i + 1}`}
            onClick={() => api?.scrollTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-5 bg-neutral-900" : "w-1.5 bg-neutral-900/25"
            }`}
          />
        ))}
      </div>
    </div>
  );
};



const QuizzesDemoSection = ({
  heading,
  description,
}: {
  heading?: string;
  description?: string;
}) => {
  return (
    <section
      data-demo-section
      className="relative flex flex-col justify-center px-5 pt-36 pb-20 md:px-16 md:py-24 lg:px-24"
    >

      <div className="relative z-10 mb-12 max-w-4xl md:mb-16">
        <Reveal as="h3" className="font-display text-3xl font-black tracking-tight text-neutral-900 md:text-5xl lg:text-6xl">
          {heading ?? "See Quizzes in action"}
        </Reveal>
        <Reveal delay={120} as="p" className="mt-6 max-w-3xl font-body text-sm leading-relaxed text-neutral-600 md:text-base lg:text-lg">
          {description ?? "Follow the quiz experience step by step, from entry screen to question formats and final result CTA."}
        </Reveal>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <QuizzesMobileCarousel className="lg:hidden" />
        <QuizzesGuidedGrid />
      </div>

    </section>
  );
};

const PollsDemoSection = ({
  heading,
  description,
}: {
  heading?: string;
  description?: string;
}) => {
  return (
    <section
      data-demo-section
      className="relative flex flex-col justify-center px-5 pt-36 pb-20 md:px-16 md:py-24 lg:px-24"
    >

      <div className="relative z-10 mb-12 max-w-4xl md:mb-16">
        <Reveal as="h3" className="font-display text-3xl font-black tracking-tight text-neutral-900 md:text-5xl lg:text-6xl">
          {heading ?? "See Polls in action"}
        </Reveal>
        <Reveal delay={120} as="p" className="mt-6 max-w-3xl font-body text-sm leading-relaxed text-neutral-600 md:text-base lg:text-lg">
          {description ?? "Follow the poll experience step by step, from start screen to different formats and final result."}
        </Reveal>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <PollsMobileCarousel className="md:hidden" />
        <div className="md:hidden mt-16">
          <div className="mx-auto w-full max-w-[420px]">
            <div className="h-px w-12 bg-neutral-900/30" />
            <p className="mt-5 font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500">
              Poll Formats
            </p>
            <h4 className="mt-4 font-display text-2xl font-black leading-[1.1] tracking-tight text-neutral-900">
              Audience input that can shape decisions
            </h4>
            <div className="mt-6 h-px w-full bg-neutral-900/10" />
            <div className="mt-6 space-y-4 font-body text-sm leading-relaxed text-neutral-600">
              <p>
                Polls give organisations a simple way to turn audience opinions into structured
                feedback. They can be used across sports, entertainment, corporate events, brand
                activations, and digital campaigns to understand what people prefer, expect, or
                want to see next.
              </p>
              <p>
                The answers can help teams, federations, event organizers, sponsors, and companies
                make more specific decisions, adjust activities, refine content, or shape future
                experiences based on real audience input.
              </p>
              <p>
                Poll formats can be adapted to the context, from simple text-based questions to
                more visual and interactive formats.
              </p>
            </div>
            <div className="mt-7 h-px w-full bg-neutral-900/10" />
            <ul className="mt-5 grid grid-cols-1 gap-y-2 font-body text-[13px] text-neutral-800">
              {[
                "Choice Polls",
                "Scale-Based Polls",
                "Drag & Drop Polls",
                "Visual Polls",
                "Campaign Polls",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="h-px w-4 bg-neutral-900/40" />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-7 h-px w-full bg-neutral-900/10" />
            <p className="mt-5 font-body text-[13px] leading-relaxed text-neutral-500">
              From quick audience checks to decision-support moments — poll logic adapts to the experience.
            </p>
          </div>
        </div>
        <div className="hidden md:grid grid-cols-1 gap-x-10 gap-y-16 sm:grid-cols-2 md:gap-x-12 md:gap-y-20 lg:grid-cols-3 lg:gap-x-14">
          {pollsDemoSteps.map((item, i) => (
            <Reveal key={item.src} delay={240 + i * 90} className="flex flex-col items-center">
              <div className="w-full max-w-[380px]">
                <div
                  className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-black shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
                  style={{ aspectRatio: "1054 / 1875" }}
                >
                  <video
                    src={item.src}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{
                      transform: `scale(${item.scale ?? 1.19})`,
                      transformOrigin: "center center",
                      objectPosition: item.objectPosition ?? "center",
                    }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                </div>
              </div>
              <p className="mt-5 font-body text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                <span className="text-neutral-900">{item.step}</span>
                <span className="mx-2 text-neutral-300">/</span>
                {item.label}
              </p>
            </Reveal>
          ))}

          <Reveal
            delay={300}
            className="sm:col-span-2 lg:col-span-1 lg:col-start-3 lg:row-start-2 lg:self-center"
          >
            <div className="mx-auto w-full max-w-[420px] lg:mx-0">
              <Reveal delay={360}>
                <div className="h-px w-12 bg-neutral-900/30" />
              </Reveal>
              <p className="mt-5 font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                Poll Formats
              </p>
              <h4 className="mt-4 font-display text-2xl font-black leading-[1.1] tracking-tight text-neutral-900 md:text-3xl">
                Audience input that can shape decisions
              </h4>
              <Reveal delay={420}>
                <div className="mt-6 h-px w-full bg-neutral-900/10" />
              </Reveal>
              <div className="mt-6 space-y-4 font-body text-sm leading-relaxed text-neutral-600">
                <p>
                  Polls give organisations a simple way to turn audience opinions into structured
                  feedback. They can be used across sports, entertainment, corporate events, brand
                  activations, and digital campaigns to understand what people prefer, expect, or
                  want to see next.
                </p>
                <p>
                  The answers can help teams, federations, event organizers, sponsors, and companies
                  make more specific decisions, adjust activities, refine content, or shape future
                  experiences based on real audience input.
                </p>
                <p>
                  Poll formats can be adapted to the context, from simple text-based questions to
                  more visual and interactive formats.
                </p>
              </div>
              <Reveal delay={480}>
                <div className="mt-7 h-px w-full bg-neutral-900/10" />
              </Reveal>
              <ul className="mt-5 grid grid-cols-1 gap-y-2 font-body text-[13px] text-neutral-800">
                {[
                  "Choice Polls",
                  "Scale-Based Polls",
                  "Drag & Drop Polls",
                  "Visual Polls",
                  "Campaign Polls",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="h-px w-4 bg-neutral-900/40" />
                    {t}
                  </li>
                ))}
              </ul>
              <Reveal delay={540}>
                <div className="mt-7 h-px w-full bg-neutral-900/10" />
              </Reveal>
              <p className="mt-5 font-body text-[13px] leading-relaxed text-neutral-500">
                From quick audience checks to decision-support moments — poll logic adapts to the experience.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const EurovisionLightshowSection = () => {
  const [playing, setPlaying] = useState(false);
  const videoId = "Nx1UaVKCeXg";
  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <section
      data-demo-section
      className="relative flex flex-col justify-center px-5 pt-40 pb-24 md:px-16 md:py-32 lg:px-24"
    >
      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16 lg:gap-20">
          {/* Left: text */}
          <Reveal className="order-2 md:order-1">
            <p className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
              Eurovision 2025
            </p>
            <div className="mt-4 h-px w-12 bg-neutral-900/30" />
            <h3 className="mt-6 font-display text-3xl font-black tracking-tight text-neutral-900 md:text-4xl lg:text-5xl">
              Lightshow at<br />Eurovision Song Contest
            </h3>
            <div className="mt-6 space-y-4">
              <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                During the Eurovision 2025 final day in Basel, Fanpictor Lightshow was used at the official watch-party in St. Jakob-Park, where more than 30,000 attendees came together to experience the event.
              </p>
              <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                Across three different Lightshow moments, the audience used their smartphones to become part of the show itself. Instead of simply watching the spectacle, attendees helped create it through synchronized light effects across the venue.
              </p>
              <p className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                The activation showed how Lightshow can turn large live audiences into part of the event experience, creating shared visual moments without additional hardware, wristbands, or complex distribution logistics.
              </p>
            </div>
            <ul className="mt-8 space-y-2.5">
              {[
                "Used during Eurovision 2025 final day in Basel",
                "Activated at the official watch-party in St. Jakob-Park",
                "More than 30,000 attendees on site",
                "Three Lightshow moments during the event",
                "Audience smartphones became part of the show",
                "No dedicated wearable hardware required",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 font-body text-sm leading-relaxed text-neutral-700">
                  <span aria-hidden className="mt-2 inline-block h-px w-4 shrink-0 bg-neutral-900/40" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 h-px w-full bg-neutral-900/10" />
            <p className="mt-6 font-display text-base font-semibold tracking-tight text-neutral-900 md:text-lg">
              Lightshow turns the audience into the visual layer of the event.
            </p>
          </Reveal>

          {/* Right: phone mockup with YouTube */}
          <Reveal delay={180} className="order-1 flex flex-col items-center md:order-2 md:items-end md:mt-16">
            <div
              className="relative w-full max-w-[320px] overflow-hidden rounded-[2.5rem] border-[10px] border-neutral-900 bg-neutral-900 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.45)]"
              style={{ aspectRatio: "9 / 19.5" }}
            >
              <div className="absolute inset-0 overflow-hidden rounded-[1.75rem] bg-black">

                {playing ? (
                  <iframe
                    title="Fanpictor Lightshow at Eurovision 2025"
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    className="group absolute inset-0 flex items-center justify-center"
                    aria-label="Play Eurovision Lightshow video"
                  >
                    <img
                      src={thumb}
                      alt="Fanpictor Lightshow at Eurovision 2025 watch-party in Basel"
                      className="absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
                      loading="lazy"
                    />
                    <span className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/20" />
                    <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105">
                      <Play className="h-6 w-6 translate-x-0.5 text-neutral-900" fill="currentColor" />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </section>
  );
};



const LightshowQRSection = ({ title = "Lightshow", featureSlug }: { title?: string; featureSlug?: string }) => {
  const { open: onSchedule } = useContactModal();

  return (
    <section className="relative flex min-h-screen items-center border-t border-foreground/10 px-5 pt-36 pb-20 md:px-16 md:py-24 lg:px-24">
      <div className="mx-auto w-full max-w-6xl text-center">
        <Reveal as="h3" className="font-display text-3xl font-black tracking-tight text-foreground md:text-5xl lg:text-6xl">
          Experience It Yourself
        </Reveal>
        <Reveal delay={120} as="p" className="mx-auto mt-6 max-w-xl font-body text-sm leading-relaxed text-muted-foreground md:text-base lg:text-lg">
          Book a free walkthrough with our experts who would be more than happy to walk you through the whole platform and help you imagine how to tailor the experience to your industry and your objectives.
        </Reveal>

        <Reveal delay={240}>
          <button
            type="button"
            onClick={onSchedule}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 border border-foreground bg-foreground px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-background transition-colors hover:bg-transparent hover:text-foreground sm:w-auto sm:justify-start"
          >
            <ExternalLink className="h-4 w-4" />
            Schedule a walkthrough
          </button>
        </Reveal>
      </div>
    </section>
  );
};

const ScrollBackground = () => {
  useEffect(() => {
    const update = () => {
      const els = document.querySelectorAll<HTMLElement>("[data-demo-section]");
      if (!els.length) return;
      const disableFadeOut = document.querySelector("[data-demo-no-fade-out]") !== null;
      const first = els[0].getBoundingClientRect();
      const last = els[els.length - 1].getBoundingClientRect();
      const top = first.top;
      const bottom = last.bottom;
      const vh = window.innerHeight;
      // Delay the grey ramp: keep background fully black until the first demo
      // section has scrolled into the upper portion of the viewport, so the
      // hero never sits on top of a half-grey background.
      const inWindow = vh * 0.55;
      const inLinear = Math.max(0, Math.min(1, (inWindow - top) / inWindow));
      const outLinear = Math.max(0, Math.min(1, bottom / (vh * 0.6)));
      const raw = Math.max(0, Math.min(1, disableFadeOut ? inLinear : Math.min(inLinear, outLinear)));
      // Smoothstep for a cinematic, gradual fade rather than a linear ramp.
      const t = raw * raw * (3 - 2 * raw);
      const c = Math.round(236 * t);
      document.documentElement.style.setProperty("--scroll-bg", `rgb(${c}, ${c}, ${c})`);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      document.documentElement.style.removeProperty("--scroll-bg");
    };
  }, []);
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10"
      style={{ backgroundColor: "var(--scroll-bg, #000)" }}
    />
  );
};

/**
 * GuidePath — a subtle, side-anchored elbow connector that animates as a
 * drawn SVG path (stroke-dashoffset). Replaces the previous centered
 * vertical/horizontal lines with an architectural, premium guide system.
 *
 * Shape (vertical/Z-elbow): drops from a side, turns 90° across the layout,
 * drops again on the opposite side — visually linking alternating
 * zig-zag sections.
 */
const ConnectorLine = ({
  orientation = "vertical",
  length = "h-24",
  className = "",
  fromSide = "left",
  tone = "dark",
}: {
  orientation?: "vertical" | "horizontal";
  length?: string;
  className?: string;
  fromSide?: "left" | "right";
  /** "dark" = stroke for light/grey sections; "light" = stroke for black sections */
  tone?: "dark" | "light";
}) => {
  const { ref, visible } = useReveal<HTMLDivElement>();

  // In-row horizontal centered line is intentionally removed — the new
  // between-section elbow path handles all visual guidance now.
  if (orientation === "horizontal") return null;

  const heightClass = length.includes("md:") ? length : `${length} md:h-28`;

  // Inset where the vertical drops sit (kept off-center, architectural).
  const insetPct = 12;
  const startX = fromSide === "left" ? insetPct : 100 - insetPct;
  const endX = fromSide === "left" ? 100 - insetPct : insetPct;
  const stroke = tone === "light" ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.22)";

  // Approximate total length for the draw animation (in % units of the
  // container, doesn't need to be exact — just bigger than the rendered
  // length so the dash fully reveals).
  const drawLen = 400;

  const segCommon = {
    stroke,
    strokeWidth: 1,
    shapeRendering: "crispEdges" as const,
    strokeDasharray: drawLen,
    strokeDashoffset: visible ? 0 : drawLen,
    style: {
      transition: "stroke-dashoffset 1600ms cubic-bezier(0.16,1,0.3,1)",
    },
  };

  return (
    <div
      ref={ref}
      aria-hidden
      className={`pointer-events-none relative w-full ${heightClass} ${className}`}
    >
      <svg
        className="absolute inset-0 h-full w-full overflow-visible"
        preserveAspectRatio="none"
        aria-hidden
      >
        {/* Drop down from starting side */}
        <line x1={`${startX}%`} y1="0" x2={`${startX}%`} y2="50%" {...segCommon} />
        {/* Horizontal cross-bar */}
        <line x1={`${startX}%`} y1="50%" x2={`${endX}%`} y2="50%" {...segCommon} />
        {/* Drop down to ending side */}
        <line x1={`${endX}%`} y1="50%" x2={`${endX}%`} y2="100%" {...segCommon} />
      </svg>
    </div>
  );
};

type ActivationContent = {
  row1: {
    landscapeLabel: string;
    eyebrow: string;
    heading: string;
    body: string[];
    bullets: string[];
    closing: string;
  };
  row2: {
    eyebrow: string;
    heading: string;
    body: string[];
    bullets: string[];
    closing: string;
    visuals: string[];
  };
  verticals: string[];
};

type BrandedFit = {
  aspectRatio?: number;
  objectFit?: "contain" | "cover";
  objectPosition?: string;
  // Controlled framing — applied inside the viewport only. Keeps the card
  // size stable and avoids distortion. Use small values (scale 1.00–1.12).
  scale?: number;
  offsetX?: string; // e.g. "0%", "-2%"
  offsetY?: string;
  rightEdgeMaskWidth?: number;
  // Vertical-only crop expressed as a fraction (0–0.4) of the source height.
  // Trims empty top/bottom bands without changing horizontal framing or card width.
  cropTop?: number;
  cropBottom?: number;
};


type BrandedSection = {
  eyebrow: string;
  headline: string;
  body: string[];
  bullets: string[];
  closing: string;
  videoUrl: string;
  videoOnLeft: boolean;
  fit?: BrandedFit;
  mediaWidthClass?: string;
};

const BRANDED_SECTIONS: BrandedSection[] = [
  {
    eyebrow: "Brand Configuration",
    headline: "Shape the experience around the brand",
    body: [
      "Fanpictor can adapt the visual identity of the audience experience around a club, event, sponsor, partner, or campaign.",
      "From interface colors and logo placement to the way engagement modules appear inside the platform, branded configuration helps each activation feel native to the organization behind it.",
      "This allows teams to create a consistent branded environment instead of treating sponsor visibility as a static logo placement.",
    ],
    bullets: [
      "Platform colors adapted to brand identity",
      "Sponsor or organization logo placement",
      "Branded interface presentation",
      "Campaign-specific visual setup",
      "Consistent experience across engagement modules",
    ],
    closing: "The experience does not just carry a brand. It becomes part of the brand moment.",
    videoUrl: brandedUiLogoAsset.url,
    videoOnLeft: true,
    fit: {
      objectFit: "contain",
      objectPosition: "center",
      scale: 1.08,
      cropTop: 0.08,
      cropBottom: 0.08,
    },

  },
  {
    eyebrow: "Sponsor Visibility",
    headline: "Give partners a visible role inside the experience",
    body: [
      "Branded Engagement allows organizations to configure sponsor placements directly inside the audience journey.",
      "A sponsor can be positioned as the main partner of an activation, making the brand visible where participation actually happens, not only around it.",
      "This creates a clearer activation asset for commercial teams to include in sponsor packages, partner campaigns, or event-specific activations.",
    ],
    bullets: [
      "Main sponsor placement setup",
      "Sponsor visibility inside the platform",
      "Campaign-specific partner naming",
      "Sponsor presence tied to interaction",
      "Clearer digital inventory for commercial teams",
    ],
    closing: "Sponsor visibility becomes stronger when it is connected to audience participation.",
    videoUrl: brandedMainSponsorAsset.url,
    videoOnLeft: false,
    fit: {
      objectFit: "contain",
      objectPosition: "center",
      scale: 1.06,
      cropTop: 0.08,
      cropBottom: 0.08,
    },
  },
  {
    eyebrow: "Branded Competition",
    headline: "Turn rankings into sponsor-owned moments",
    body: [
      "Leaderboards create natural attention because audiences return to see progress, rankings, and results.",
      "By assigning sponsor visibility to leaderboard moments, organizations can give partners ownership of recurring attention points across a campaign, matchday, event, or season.",
      "This helps transform competition mechanics into branded inventory that can be measured through participation and repeat engagement.",
    ],
    bullets: [
      "Sponsor visibility on leaderboard experiences",
      "Recurring branded attention moments",
      "Ranking-based engagement opportunities",
      "Campaign and season-long activation potential",
      "Measurable participation around sponsor-owned formats",
    ],
    closing: "The more often fans return, the more valuable the branded moment becomes.",
    videoUrl: brandedLeaderboardAsset.url,
    videoOnLeft: true,
    fit: {
      objectFit: "contain",
      objectPosition: "center",
      scale: 1.06,
      cropTop: 0.08,
      cropBottom: 0.08,
    },
  },
  {
    eyebrow: "Sponsored Voting",
    headline: "Connect branded voting to commercial opportunity",
    body: [
      "MVP Voting can become a sponsor-owned interaction where fans actively engage with players, performers, or moments.",
      "This creates a stronger commercial story than simple visibility. If fans repeatedly vote for a specific player, team, artist, or performer, that behavior can guide a more relevant sponsor campaign.",
      "For example, if a player receives strong voting activity, the organization can use that signal to propose a player-related merchandise offer, sponsor giveaway, or targeted campaign.",
    ],
    bullets: [
      "MVP Voting as a branded sponsor moment",
      "Sponsor visibility connected to fan choice",
      "Audience behavior that reveals preference",
      "Player or performer-specific campaign ideas",
      "Stronger justification for sponsor activations",
    ],
    closing: "The value is not only in the vote. It is in what the vote reveals.",
    videoUrl: brandedMvpCtaAsset.url,
    videoOnLeft: false,
    fit: {
      objectFit: "contain",
      objectPosition: "center",
      scale: 1.06,
      cropTop: 0.08,
      cropBottom: 0.08,
    },
  },
  {
    eyebrow: "Branded Response Moments",
    headline: "Use answers as the starting point for activation",
    body: [
      "Quizzes and polls give sponsors a way to appear inside active audience moments, not passive placements.",
      "A quiz or poll can be branded around a partner, campaign, or sponsor message, while the completion moment can guide users toward a relevant next step.",
      "This makes smaller interactions commercially useful because every answer, vote, and completion can become part of a measurable activation flow.",
    ],
    bullets: [
      "Branded quiz and poll moments",
      "Sponsor visibility during participation",
      "CTA placement after completion",
      "Audience responses tied to campaign logic",
      "Measurable interaction around sponsor content",
    ],
    closing: "When audiences answer, sponsors learn what they can act on.",
    videoUrl: brandedQuizPollCtaAsset.url,
    videoOnLeft: true,
    fit: {
      objectFit: "contain",
      objectPosition: "center",
      scale: 1.06,
      cropTop: 0.08,
      cropBottom: 0.08,
    },
  },
  {
    eyebrow: "Sponsored Feature Example",
    headline: "Package features as branded activation assets",
    body: [
      "Fanpictor features can be renamed and positioned around a sponsor, campaign, or partner.",
      "Instead of offering a generic engagement module, teams can package a specific branded experience, such as a sponsor-powered quiz, voting moment, poll, prediction, or campaign game.",
      "This gives commercial teams a clearer product to sell and gives sponsors a more meaningful role in the audience experience.",
    ],
    bullets: [
      "Sponsored quiz and game formats",
      "Campaign-specific feature naming",
      "Sponsor-owned engagement modules",
      "Digital inventory beyond static logo placement",
      "Activation assets that can be repeated and reported",
    ],
    closing: "Branded features are easier to sell when they are easy to understand.",
    videoUrl: brandedSponsoredGameAsset.url,
    videoOnLeft: false,
    fit: {
      objectFit: "contain",
      objectPosition: "center",
      scale: 1.18,
      cropTop: 0.08,
      cropBottom: 0.08,
    },
  },
];

const BrandedVideo = ({ src, fit }: { src: string; fit?: BrandedFit }) => {
  const [naturalRatio, setNaturalRatio] = useState<number>(16 / 9);
  const objectFit = fit?.objectFit ?? "contain";
  const position = fit?.objectPosition ?? "center";
  const scale = Math.max(1, Math.min(1.25, fit?.scale ?? 1));
  const offsetX = fit?.offsetX ?? "0%";
  const offsetY = fit?.offsetY ?? "0%";
  const rightEdgeMaskWidth = fit?.rightEdgeMaskWidth ?? 0;

  // Vertical-only crop. Trims empty top/bottom bands from the source video
  // while keeping the card width and horizontal framing untouched.
  const cropTop = Math.max(0, Math.min(0.4, fit?.cropTop ?? 0));
  const cropBottom = Math.max(0, Math.min(0.4, fit?.cropBottom ?? 0));
  const visibleV = Math.max(0.4, 1 - cropTop - cropBottom);

  // Card aspect: source natural ratio divided by the visible vertical fraction.
  // This shortens the card height while preserving its width — only the empty
  // top/bottom bands of the source disappear.
  const baseRatio = fit?.aspectRatio ?? naturalRatio;
  const ratio = baseRatio / visibleV;

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-neutral-100 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)]"
      style={{ aspectRatio: `${ratio}` }}
    >
      <div className="absolute inset-x-0 overflow-hidden" style={{ top: 0, bottom: 0 }}>
        {/* Inner stage matches the source's natural aspect ratio so the video
            fills it exactly. Shifted upward by cropTop so the empty top band
            sits above the card and the empty bottom band sits below it. */}
        <div
          className="absolute left-0 right-0"
          style={{
            height: `${100 / visibleV}%`,
            top: `${(-cropTop * 100) / visibleV}%`,
          }}
        >
          <video
            src={src}
            autoPlay
            muted
            loop
            playsInline
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (v.videoWidth && v.videoHeight) {
                setNaturalRatio(v.videoWidth / v.videoHeight);
              }
            }}
            className={`absolute inset-0 h-full w-full ${objectFit === "cover" ? "object-cover" : "object-contain"}`}
            style={{
              objectPosition: position,
              transform: `translate(${offsetX}, ${offsetY}) scale(${scale})`,
              transformOrigin: "center center",
            }}
          />
        </div>
      </div>
      {rightEdgeMaskWidth > 0 && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10"
          style={{
            width: `${rightEdgeMaskWidth}px`,
            backgroundColor: "var(--scroll-bg, hsl(var(--background)))",
          }}
        />
      )}
    </div>
  );
};


const BrandedTextBlock = ({
  eyebrow,
  headline,
  body,
  bullets,
  closing,
}: {
  eyebrow: string;
  headline: string;
  body: string[];
  bullets: string[];
  closing: string;
}) => (
  <div className="w-full">
    <p className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">{eyebrow}</p>
    <div className="mt-4 h-px w-12 bg-neutral-900/30" />
    <h4 className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
      {headline}
    </h4>
    <div className="mt-6 space-y-4">
      {body.map((p, i) => (
        <p key={i} className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
          {p}
        </p>
      ))}
    </div>
    <div className="mt-8 h-px w-full bg-neutral-900/10" />
    <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-3 font-body text-sm text-neutral-700">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-500" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <div className="mt-8 h-px w-full bg-neutral-900/10" />
    <p className="mt-6 font-display text-lg font-semibold tracking-tight text-neutral-900 md:text-xl">
      {closing}
    </p>
  </div>
);

const BrandedEngagementSection = ({
  heading,
  description,
}: {
  heading?: string;
  description?: string;
}) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const accordionItems = [
    {
      title: "Branded Moments",
      body: "Interactive features can be shaped around sponsors, campaigns, or partners.",
    },
    {
      title: "Audience Signals",
      body: "Participation reveals what audiences respond to, choose, vote for, or return to.",
    },
    {
      title: "Activation Value",
      body: "Commercial teams can package, report, and improve sponsor-owned experiences over time.",
    },
  ];

  const toggleAccordion = (title: string) => {
    setOpenAccordion((prev) => (prev === title ? null : title));
  };

  return (
    <>
    <section
      data-demo-section
      className="relative flex flex-col justify-center px-5 pt-36 pb-20 md:px-16 md:py-24 lg:px-24"
    >
      <div className="relative z-10 mb-12 max-w-4xl md:mb-16">
        <Reveal as="p" className="font-body text-[11px] font-medium uppercase tracking-[0.32em] text-neutral-500">
          A guided journey
        </Reveal>
        <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-neutral-900 md:text-5xl lg:text-6xl">
          {heading ?? "A guided walkthrough"}
        </Reveal>
        {description && (
          <Reveal delay={160} as="p" className="mt-6 max-w-3xl font-body text-sm leading-relaxed text-neutral-600 md:text-base lg:text-lg">
            {description}
          </Reveal>
        )}
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[92rem]">
        <div className="flex flex-col items-stretch gap-16 md:gap-20 lg:gap-24">
          {BRANDED_SECTIONS.map((s, idx) => (
            <div key={s.headline} className="flex flex-col gap-12 md:gap-16">
              {idx === 0 ? (
                // Editorial, left-weighted layout for the opening section:
                // video shifted slightly off-center to the left, text sits
                // on the right with tighter width and natural breathing room.
                <Reveal
                  delay={120}
                  className="flex flex-col items-center gap-10 md:grid md:grid-cols-12 md:items-center md:gap-10 lg:gap-14"
                >
                  <div className="w-full md:col-span-7 md:col-start-1 lg:col-span-7 lg:-ml-4 xl:-ml-8">
                    <BrandedVideo src={s.videoUrl} fit={s.fit} />
                  </div>
                  <div className="w-full md:col-span-5 md:col-start-8 lg:col-span-4 lg:col-start-9">
                    <BrandedTextBlock
                      eyebrow={s.eyebrow}
                      headline={s.headline}
                      body={s.body}
                      bullets={s.bullets}
                      closing={s.closing}
                    />
                  </div>
                </Reveal>
              ) : (
                <Reveal
                  delay={120}
                  className={`flex flex-col items-center gap-10 md:items-center md:gap-8 lg:gap-12 ${
                    s.videoOnLeft ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div className={`w-full md:shrink-0 ${s.mediaWidthClass ?? "md:w-[64%]"}`}>
                    <BrandedVideo src={s.videoUrl} fit={s.fit} />
                  </div>
                  <div className="hidden md:flex md:flex-1 md:items-center md:justify-center">
                    <ConnectorLine orientation="horizontal" length="w-full" />
                  </div>
                  <div className="w-full md:w-[36%]">
                    <BrandedTextBlock
                      eyebrow={s.eyebrow}
                      headline={s.headline}
                      body={s.body}
                      bullets={s.bullets}
                      closing={s.closing}
                    />
                  </div>
                </Reveal>
              )}
              {idx < BRANDED_SECTIONS.length - 1 && (
                <ConnectorLine
                  orientation="vertical"
                  length="h-20 md:h-28"
                  fromSide={idx % 2 === 0 ? "right" : "left"}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Closing section — transparent so the page's fixed ScrollBackground
        shows through. The same mechanism that fades black→grey at the top
        of the page (entering the first data-demo-section) now fades
        grey→black at the bottom (exiting the last data-demo-section).
        A tall top spacer ensures the bg has already resolved to pure black
        before the Commercial Value content scrolls into view, so it sits
        on clean black with no horizontal band or overlay strip. */}
    <section
      className="relative px-4 pb-24 pt-[44vh] text-foreground md:px-16 md:pb-32 md:pt-[50vh] lg:px-24 lg:pb-40"
    >
      <div className="mx-auto w-full max-w-5xl text-center">
        <Reveal>
          <p className="font-body text-[11px] font-medium uppercase tracking-[0.32em] text-neutral-400">
            Commercial Value
          </p>
          <div className="mx-auto mt-4 h-px w-12 bg-foreground/30" />
          <h4 className="mt-6 font-display text-3xl font-black tracking-tight text-foreground md:text-4xl lg:text-5xl">
            From branded visibility to measurable inventory
          </h4>
          <div className="mt-8 space-y-5 text-left md:text-center">
            <p className="font-body text-sm leading-relaxed text-neutral-300 md:text-base">
              Branded Engagement helps organizations create sponsor-owned moments that audiences actively participate in.
            </p>
            <p className="font-body text-sm leading-relaxed text-neutral-300 md:text-base">
              This gives commercial teams a stronger way to package digital inventory. A sponsor can own a quiz, voting moment, leaderboard, prediction, poll, or campaign experience, while the organization can measure how audiences interacted with it.
            </p>
            <p className="font-body text-sm leading-relaxed text-neutral-300 md:text-base">
              The result is a clearer connection between sponsor visibility, audience behavior, and future activation opportunities.
            </p>
          </div>

          {/* Mobile accordion */}
          <div className="mt-12 md:hidden">
            {accordionItems.map((item, idx) => (
              <div
                key={item.title}
                className={`border-b border-foreground/10 ${idx === 0 ? "border-t" : ""}`}
              >
                <button
                  onClick={() => toggleAccordion(item.title)}
                  className="flex w-full items-center justify-between py-5 text-left"
                >
                  <h5 className="font-display text-lg font-black tracking-tight text-foreground">
                    {item.title}
                  </h5>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-neutral-400 transition-transform duration-300 ${
                      openAccordion === item.title ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    openAccordion === item.title ? "max-h-40 pb-5" : "max-h-0"
                  }`}
                >
                  <p className="font-body text-sm leading-relaxed text-neutral-300">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop grid */}
          <div className="mt-12 hidden grid-cols-3 gap-12 text-left md:grid">
            {accordionItems.map((col) => (
              <div key={col.title}>
                <div className="h-px w-10 bg-foreground/30" />
                <h5 className="mt-5 font-display text-lg font-black tracking-tight text-foreground md:text-xl">
                  {col.title}
                </h5>
                <p className="mt-3 font-body text-sm leading-relaxed text-neutral-300">{col.body}</p>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-14 h-px w-full max-w-3xl bg-foreground/15" />
          <p className="mt-8 font-display text-lg font-semibold tracking-tight text-foreground md:text-xl lg:text-2xl">
            Engagement creates attention. Branding gives that attention an owner. Data makes that ownership measurable.
          </p>
        </Reveal>
      </div>
    </section>
    </>
  );
};

const AudienceActivationSection = ({
  variant,
  heading,
  description,
}: {
  variant: "branded" | "rewards";
  heading?: string;
  description?: string;
}) => {
  const content: ActivationContent =
    variant === "branded"
      ? {
          row1: {
            landscapeLabel: "Branding Configuration Video Coming Soon",
            eyebrow: "Branding Configuration",
            heading: "Create moments partners can own",
            body: [
              "Branded Engagement allows teams to configure interactive experiences around the identity of a sponsor, partner, organization, or campaign.",
              "A quiz, poll, vote, prediction, or Pulse activation can become more than a generic engagement feature. It can become a sponsor-owned moment with clear visibility, audience participation, and measurable interaction.",
              "This gives commercial teams a new type of digital activation inventory that can be packaged into sponsorship, partnership, or campaign proposals.",
            ],
            bullets: [
              "Sponsor-owned quizzes, polls, votes, and predictions",
              "Custom feature names and campaign labels",
              "Brand colors, logos, and sponsor visibility",
              "“Powered by” and “Presented by” placements",
              "Branded moments across the audience journey",
              "Measurable interaction around each activation",
            ],
            closing: "Engagement creates attention. Branding gives that attention an owner.",
          },
          row2: {
            eyebrow: "Activation Example",
            heading: "Use audience behavior to shape sponsor campaigns",
            body: [
              "Branded moments become more valuable when they are connected to real audience behavior.",
              "For example, if MVP Voting shows that a large share of fans repeatedly vote for a specific player, the organization can use that insight to create a more relevant partner activation.",
              "Instead of offering a generic sponsor placement, the commercial team can propose a campaign tied to the player fans already engage with — such as a merchandise offer, player-specific kit discount, sponsor giveaway, or targeted follow-up campaign.",
            ],
            bullets: [
              "Identify which players, moments, or formats fans engage with most",
              "Package those insights into sponsor activation ideas",
              "Connect branded moments to merchandise, ticketing, or partner offers",
              "Give sponsors a clearer reason to invest in digital inventory",
              "Report back with participation and engagement data",
            ],
            closing: "The value is not only in the branded moment. It is in what the audience does with it.",
            visuals: ["MVP Voting Insight", "Sponsor Activation Mockup"],
          },
          verticals: [
            "Branded Quiz Experience",
            "Sponsored MVP Voting",
            "Campaign-Branded Poll",
          ],
        }
      : {
          row1: {
            landscapeLabel: "CTA & Reward Configuration Video Coming Soon",
            eyebrow: "Reward Configuration",
            heading: "Design the next step after participation",
            body: [
              "Rewards & Incentives allow teams to decide what happens after an audience member participates.",
              "A user can be guided toward a prize, a sponsor offer, a promo code, a ticketing page, a merchandise campaign, or another follow-up action based on how they interacted with the experience.",
              "This turns engagement from a one-time interaction into a structured activation flow.",
            ],
            bullets: [
              "CTA setup across engagement features",
              "Winner and consolation prize logic",
              "Sponsor offers and promo codes",
              "Feature-specific reward paths",
              "Campaign links after participation",
              "Measurable user actions after engagement",
            ],
            closing: "Engagement creates the moment. Incentives move the audience forward.",
          },
          row2: {
            eyebrow: "Activation Flow",
            heading: "Move from participation to measurable value",
            body: [
              "When users engage with a feature, Fanpictor can help teams turn that interaction into a relevant next action.",
              "For example, after fans vote for a player in MVP Voting, the organization can connect that behavior to a targeted offer — such as a discount on that player’s shirt, a sponsor-backed giveaway, or a limited campaign connected to the voting result.",
              "The same logic can be used across quizzes, polls, predictions, Pulse activations, and other audience moments.",
            ],
            bullets: [
              "Vote for a player → merchandise offer",
              "Complete a quiz → sponsor discount code",
              "Join Pulse → prize or consolation reward",
              "Submit a prediction → ticket or partner campaign",
              "Answer a poll → audience-specific follow-up",
            ],
            closing: "The strongest activations are built around what the audience has already shown interest in.",
            visuals: ["Voting → Offer Flow", "Sponsor Reward Mockup"],
          },
          verticals: [
            "CTA After Participation",
            "Prize Winner Flow",
            "Sponsor Offer Redemption",
          ],
        };

  const LandscapePlaceholder = ({ label }: { label: string }) => (
    <div
      className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-neutral-50 shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
      style={{ aspectRatio: "16 / 9" }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="font-body text-[10px] uppercase tracking-[0.22em] text-neutral-400">Placeholder</span>
        <span className="font-display text-sm font-semibold tracking-tight text-neutral-700 md:text-base">{label}</span>
        <span className="font-body text-[11px] uppercase tracking-[0.2em] text-neutral-400">Video Coming Soon</span>
      </div>
    </div>
  );

  const VerticalPlaceholder = ({ label, small = false }: { label: string; small?: boolean }) => (
    <div className="flex flex-col items-center">
      <div className={`w-full ${small ? "max-w-[240px]" : "max-w-[300px]"}`}>
        <div
          className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-neutral-50 shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)]"
          style={{ aspectRatio: "975 / 1874" }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
            <span className="font-body text-[10px] uppercase tracking-[0.22em] text-neutral-400">Placeholder</span>
            <span className="font-display text-xs font-semibold tracking-tight text-neutral-700">Video Coming Soon</span>
          </div>
        </div>
      </div>
      <p className="mt-6 font-body text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</p>
    </div>
  );

  const TextBlock = ({
    eyebrow,
    headline,
    body,
    bullets,
    closing,
  }: {
    eyebrow: string;
    headline: string;
    body: string[];
    bullets: string[];
    closing: string;
  }) => (
    <div className="w-full">
      <p className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">{eyebrow}</p>
      <div className="mt-4 h-px w-12 bg-neutral-900/30" />
      <h4 className="mt-6 font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
        {headline}
      </h4>
      <div className="mt-6 space-y-4">
        {body.map((p, i) => (
          <p key={i} className="font-body text-sm leading-relaxed text-neutral-600 md:text-[15px]">
            {p}
          </p>
        ))}
      </div>
      <div className="mt-8 h-px w-full bg-neutral-900/10" />
      <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 font-body text-sm text-neutral-700">
            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-500" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8 h-px w-full bg-neutral-900/10" />
      <p className="mt-6 font-display text-lg font-semibold tracking-tight text-neutral-900 md:text-xl">
        {closing}
      </p>
    </div>
  );

  return (
    <section
      data-demo-section
      className="relative flex flex-col justify-center px-5 pt-36 pb-20 md:px-16 md:py-24 lg:px-24"
    >
      <div className="relative z-10 mb-12 max-w-4xl md:mb-16">
        <Reveal as="p" className="font-body text-[11px] font-medium uppercase tracking-[0.32em] text-neutral-500">
          A guided journey
        </Reveal>
        <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-neutral-900 md:text-5xl lg:text-6xl">
          {heading ?? "A guided walkthrough"}
        </Reveal>
        <Reveal delay={160} as="p" className="mt-6 max-w-3xl font-body text-sm leading-relaxed text-neutral-600 md:text-base lg:text-lg">
          {description ?? ""}
        </Reveal>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <div className="flex flex-col items-stretch gap-16 md:gap-20 lg:gap-24">
          {/* Row 1: Configuration walkthrough — video left, text right */}
          <Reveal delay={120} className="flex flex-col items-center gap-10 md:flex-row md:items-center md:gap-10 lg:gap-14">
            <div className="w-full md:w-[58%] md:shrink-0">
              <LandscapePlaceholder label={content.row1.landscapeLabel} />
            </div>
            <div className="hidden md:flex md:flex-1 md:items-center md:justify-center">
              <ConnectorLine orientation="horizontal" length="w-full" />
            </div>
            <div className="w-full md:w-[42%]">
              <TextBlock
                eyebrow={content.row1.eyebrow}
                headline={content.row1.heading}
                body={content.row1.body}
                bullets={content.row1.bullets}
                closing={content.row1.closing}
              />
            </div>
          </Reveal>

          {/* Elbow connector from Row 1 → Row 2 */}
          <ConnectorLine
            orientation="vertical"
            length="h-20 md:h-28"
            fromSide="right"
          />

          {/* Row 2: Example commercial flow — text left, visuals right */}
          <Reveal delay={120} className="flex flex-col items-center gap-10 md:flex-row-reverse md:items-center md:gap-10 lg:gap-14">
            <div className="w-full md:w-[55%] md:shrink-0">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
                {content.row2.visuals.map((label) => (
                  <VerticalPlaceholder key={label} label={label} small />
                ))}
              </div>
            </div>
            <div className="w-full md:w-[45%]">
              <TextBlock
                eyebrow={content.row2.eyebrow}
                headline={content.row2.heading}
                body={content.row2.body}
                bullets={content.row2.bullets}
                closing={content.row2.closing}
              />
            </div>
          </Reveal>

          {/* Elbow connector from Row 2 → Row 3 */}
          <ConnectorLine
            orientation="vertical"
            length="h-20 md:h-28"
            fromSide="left"
          />

          {/* Row 3: Three vertical front-end previews */}
          <div>
            <Reveal as="p" className="mb-3 text-center font-body text-[11px] font-medium uppercase tracking-[0.32em] text-neutral-500">
              Front-end Examples
            </Reveal>
            <Reveal delay={80} as="h4" className="mb-12 text-center font-display text-2xl font-black tracking-tight text-neutral-900 md:text-3xl">
              How it appears to the audience
            </Reveal>
            <Reveal delay={160}>
              <div className="grid grid-cols-1 gap-12 sm:grid-cols-3 md:gap-14 lg:gap-20">
                {content.verticals.map((label) => (
                  <VerticalPlaceholder key={label} label={label} />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

type ConfigSectionContent = {
  eyebrow: string;
  headline: string;
  body: string[];
  bullets: string[];
  closing: string;
  videoSrc: string;
  videoTopCropPct?: number;
  videoBottomCropPct?: number;
  videoSideCropPct?: number;
};

const configurationContent: Record<"design" | "content", ConfigSectionContent[]> = {
  design: [
    {
      eyebrow: "ACCESS EXPERIENCE DESIGN",
      headline: "Shape the first touchpoint around the brand",
      body: [
        "The access layer is often the first branded moment a participant sees. Fanpictor allows this entry experience to be configured around the visual identity of the organization, event, sponsor, or campaign.",
        "Backgrounds can be set as responsive images for desktop and mobile, or defined through color values such as HEX and RGB. Logos, buttons, dropdowns, borders, language selectors, error states, and visible interface components can all be adapted so the experience feels native from the first screen.",
      ],
      bullets: [
        "Responsive desktop and mobile backgrounds",
        "Logo, button, dropdown, and border styling",
        "Configurable colors, states, and visible components",
      ],
      closing: "The experience starts before participation begins.",
      videoSrc: designConfigLoginAsset.url,
      videoTopCropPct: 8,
      videoBottomCropPct: 8,
    },
    {
      eyebrow: "FEATURE DESIGN",
      headline: "Configure each interaction without breaking the experience",
      body: [
        "Each engagement feature can carry its own visual identity while still remaining part of the wider platform experience. In this example, MVP Voting is configured across the main feature page, countdown flow, player selection, live standings, voting result, and social sharing screens.",
        "Teams can adapt backgrounds, colors, sponsor logo placements, and interface components so the feature fits the specific campaign, event, or partner activation without requiring a separate product build.",
      ],
      bullets: [
        "Feature-specific backgrounds and colors",
        "Sponsor visibility inside the experience",
        "Configurable screens across the full participation journey",
      ],
      closing: "Every feature can be adapted while the platform logic stays consistent.",
      videoSrc: designConfigGameAsset.url,
      videoTopCropPct: 8,
      videoBottomCropPct: 8,
    },
    {
      eyebrow: "LEADERBOARD DESIGN",
      headline: "Make rankings feel native to the campaign",
      body: [
        "Leaderboards are recurring attention points because audiences return to compare progress, rankings, and results. Fanpictor allows the leaderboard experience to be visually configured so it matches the campaign, sponsor, club, or event identity.",
        "Backgrounds, main logos, headers, text colors, table styling, ranking components, and visual states can all be adjusted to make the leaderboard feel like part of the activation rather than a generic results table.",
      ],
      bullets: [
        "Configurable background, logo, and header styling",
        "Ranking table and component customization",
        "Visual consistency across recurring engagement moments",
      ],
      closing: "A leaderboard becomes more valuable when it feels owned by the activation.",
      videoSrc: designConfigLeaderboardAsset.url,
      videoTopCropPct: 8,
      videoBottomCropPct: 8,
    },
  ],
  content: [
    {
      eyebrow: "ACCESS CONTENT",
      headline: "Control the language of the first interaction",
      body: [
        "The first interaction should not feel generic. Fanpictor allows teams to customize the written content across the login, registration, profile, forgot password, email, and entry flows so the wording matches the audience, campaign, and organization.",
        "Headlines, descriptions, button labels, instructions, language selector text, dropdown labels, error messages, and email-related content can be adjusted without changing the underlying platform structure.",
      ],
      bullets: [
        "Custom headlines, instructions, and button labels",
        "Registration, profile, and entry page wording",
        "Error messages and email-related content",
      ],
      closing: "The same platform can speak in the voice of each activation.",
      videoSrc: contentConfigLoginAsset.url,
      videoTopCropPct: 8,
    },
    {
      eyebrow: "FEATURE CONTENT",
      headline: "Adapt the wording around every participation flow",
      body: [
        "Every engagement flow depends on clear wording. In this example, the MVP Voting experience is configured from a content perspective across the main feature page, countdown flow, player selection, live standings, voting result, and social sharing screens.",
        "This allows teams to adapt labels, instructions, button text, result messages, and campaign-specific naming so each feature feels relevant to the audience and the activation goal.",
      ],
      bullets: [
        "Custom feature names, labels, and instructions",
        "Adapted result, countdown, and sharing copy",
        "Campaign-specific language across the user journey",
      ],
      closing: "The interaction logic stays the same, but the story can change for every campaign.",
      videoSrc: contentConfigGameAsset.url,
      videoTopCropPct: 8,
      videoBottomCropPct: 8,
    },
    {
      eyebrow: "LEADERBOARD CONTENT",
      headline: "Turn rankings into a campaign-specific experience",
      body: [
        "Leaderboards are not only about numbers. The way rankings are named, explained, and presented affects how audiences understand the competition and why they return.",
        "Fanpictor allows teams to configure leaderboard titles, table labels, rank text, points labels, scroll messages, descriptions, and campaign-specific wording. This makes the leaderboard easier to connect to sponsors, seasonal campaigns, event formats, or branded competition mechanics.",
      ],
      bullets: [
        "Configurable leaderboard titles and table labels",
        "Custom rank, points, and scroll text",
        "Campaign-specific wording for recurring competition",
      ],
      closing: "A leaderboard becomes clearer when the language matches the activation.",
      videoSrc: contentConfigLeaderboardAsset.url,
      videoTopCropPct: 8,
      videoBottomCropPct: 8,
    },
  ],
};

const ConfigurationSection = ({ variant }: { variant: "design" | "content" }) => {
  const sections = configurationContent[variant];
  const { open: openContact } = useContactModal();

  return (
    <>
      <section
        data-demo-section
        className="relative px-5 pt-36 pb-[40vh] md:px-16 md:pt-28 md:pb-[44vh] lg:px-24 lg:pt-32"
      >
        <div className="relative z-10 mx-auto flex max-w-8xl flex-col gap-24 md:gap-32">
          {sections.map((s, idx) => {
            const videoOnLeft = idx % 2 === 0;
            const topCrop = Math.max(0, Math.min(40, s.videoTopCropPct ?? 0)) / 100;
            const bottomCrop = Math.max(0, Math.min(40, s.videoBottomCropPct ?? 0)) / 100;
            const sideCrop = Math.max(0, Math.min(40, s.videoSideCropPct ?? 0)) / 100;
            const visibleRatio = 1 - topCrop - bottomCrop;
            const hVisibleRatio = 1 - sideCrop * 2;
            const VideoBlock = (
              <Reveal delay={120} className="w-full">
                <div
                  className="relative w-full overflow-hidden rounded-2xl border border-neutral-900/10 bg-neutral-50 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]"
                  style={{ aspectRatio: `${16 * hVisibleRatio} / ${9 * visibleRatio}` }}
                >
                  <video
                    src={s.videoSrc}
                    className="absolute object-contain"
                    style={{
                      width: `${100 / hVisibleRatio}%`,
                      left: `-${(sideCrop * 100) / hVisibleRatio}%`,
                      height: `${100 / visibleRatio}%`,
                      top: `-${(topCrop * 100) / visibleRatio}%`,
                      transform: "scale(1.03)",
                      transformOrigin: "center",
                    }}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                </div>
              </Reveal>
            );


            const TextBlock = (
              <div className="w-full">
                <Reveal>
                  <div className="h-px w-12 bg-neutral-900/30" />
                  <p className="mt-5 font-body text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
                    {s.eyebrow}
                  </p>
                </Reveal>
                <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-neutral-900 md:text-4xl lg:text-5xl">
                  {s.headline}
                </Reveal>
                <Reveal delay={160} className="mt-6 space-y-4">
                  {s.body.map((p, i) => (
                    <p key={i} className="font-body text-sm leading-relaxed text-neutral-700 md:text-base">
                      {p}
                    </p>
                  ))}
                </Reveal>
                <Reveal delay={240} className="mt-8">
                  <div className="h-px w-full bg-neutral-900/10" />
                  <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3 font-body text-sm text-neutral-800">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-500" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={320} className="mt-8">
                  <div className="h-px w-full bg-neutral-900/10" />
                  <p className="mt-6 font-display text-lg font-semibold tracking-tight text-neutral-900 md:text-xl">
                    {s.closing}
                  </p>
                </Reveal>
              </div>
            );

            return (
              <div
                key={idx}
                className={`grid grid-cols-1 items-center gap-10 lg:gap-12 ${videoOnLeft ? 'lg:grid-cols-[11fr_10fr]' : 'lg:grid-cols-[10fr_11fr]'}`}
              >
                <div className={videoOnLeft ? "lg:order-1" : "lg:order-2"}>{VideoBlock}</div>
                <div className={videoOnLeft ? "lg:order-2" : "lg:order-1"}>{TextBlock}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="relative flex items-center px-4 py-24 text-foreground md:px-16 md:py-32 lg:px-24">

        <div className="mx-auto grid w-full max-w-6xl items-center gap-12 md:grid-cols-[1.2fr_1fr] md:gap-16">
          <div className="text-left">
            <Reveal>
              <div className="h-px w-12 bg-white/30" />
              <p className="mt-5 font-body text-[11px] font-medium uppercase tracking-[0.32em] text-neutral-400">
                Real-time configuration
              </p>
            </Reveal>
            <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-white md:text-5xl lg:text-6xl">
              See configuration in real time
            </Reveal>
            <Reveal delay={160} as="p" className="mt-6 max-w-xl font-body text-sm leading-relaxed text-neutral-300 md:text-base lg:text-lg">
              Want to see how Fanpictor can be adapted around your brand, campaign, or audience journey? Book a free walkthrough with our team and we'll show how design, content, and feature settings can be configured live.
            </Reveal>
            <Reveal delay={240}>
              <button
                type="button"
                onClick={openContact}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 border border-white bg-white px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-transparent hover:text-white sm:w-auto sm:justify-start"
              >
                <ExternalLink className="h-4 w-4" />
                Schedule a walkthrough
              </button>
            </Reveal>
          </div>
          <Reveal delay={320} className="md:justify-self-end">
            <div className="w-full max-w-sm border border-white/15 bg-white/[0.03] p-8 md:p-10">
              <div className="h-px w-10 bg-white/30" />
              <p className="mt-5 font-display text-lg font-semibold tracking-tight text-white md:text-xl">
                A guided session with our experts
              </p>
              <p className="mt-3 font-body text-sm leading-relaxed text-neutral-400">
                Tailored to your use case — design, written content, and feature configuration walked through step by step.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};


const AudienceInsightsCTA = () => {
  const { open: openContact } = useContactModal();
  return (
    <section className="relative flex items-center px-4 py-24 text-foreground md:px-16 md:py-32 lg:px-24">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-12 md:grid-cols-[1.2fr_1fr] md:gap-16">
        <div className="text-left">
          <Reveal>
            <div className="h-px w-12 bg-white/30" />
            <p className="mt-5 font-body text-[11px] font-medium uppercase tracking-[0.32em] text-neutral-400">
              Audience data walkthrough
            </p>
          </Reveal>
          <Reveal delay={80} as="h3" className="mt-4 font-display text-3xl font-black tracking-tight text-white md:text-5xl lg:text-6xl">
            See what your audience data can reveal
          </Reveal>
          <Reveal delay={160} as="p" className="mt-6 max-w-xl font-body text-sm leading-relaxed text-neutral-300 md:text-base lg:text-lg">
            Want to understand what Fanpictor can capture across audience profiles, participation behavior, and engagement history? Book a free walkthrough with our team and we'll show how the data can be read, compared, and used to support stronger targeting, activations, and partner conversations.
          </Reveal>
          <Reveal delay={240}>
            <button
              type="button"
              onClick={openContact}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 border border-white bg-white px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-transparent hover:text-white sm:w-auto sm:justify-start"
            >
              <ExternalLink className="h-4 w-4" />
              Schedule a walkthrough
            </button>
          </Reveal>
        </div>
        <Reveal delay={320} className="md:justify-self-end">
          <div className="w-full max-w-sm border border-white/15 bg-white/[0.03] p-8 md:p-10">
            <div className="h-px w-10 bg-white/30" />
            <p className="mt-5 font-display text-lg font-semibold tracking-tight text-white md:text-xl">
              A guided session with our experts
            </p>
            <p className="mt-3 font-body text-sm leading-relaxed text-neutral-400">
              We'll walk through the data Fanpictor gathers, how it is structured, and how teams can use it to better understand audiences, improve activations, and create more relevant sponsor opportunities.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const DemoFeature = () => {

  const params = useParams();
  const moduleSlug = params.moduleSlug || "audience-engagement";
  const submoduleSlug = params.submoduleSlug || "quizzes";
  const navigate = useNavigate();
  const { open: openContact } = useContactModal();

  const result = findSubmodule(moduleSlug || "", submoduleSlug || "");
  const mod = result.mod;
  const sub = result.sub;

  if (!mod || !sub) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-4 font-display text-2xl font-bold text-foreground">Feature not found</h1>
          <Link to="/demo" className="font-body text-sm text-muted-foreground hover:text-foreground">
            ← Back to Demo
          </Link>
        </div>
      </div>
    );
  }

  const useScrollBg = mod.slug === "audience-engagement" || mod.slug === "audience-insights" || mod.slug === "audience-activation" || mod.slug === "configuration-panel";

  return (
    <div className={`flex min-h-svh flex-col ${useScrollBg ? "bg-transparent" : "bg-background"}`}>
      {useScrollBg && <ScrollBackground />}

      {/* Header */}
      <SiteHeader showMenu={false} />
      <SiteHeaderSpacer />

      {/* Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <section
          className={`relative overflow-hidden px-5 sm:px-8 md:px-16 lg:px-24 ${
            ["audience-engagement", "audience-insights", "audience-activation", "configuration-panel"].includes(mod.slug)
              ? "flex min-h-[70vh] items-center pb-40 pt-10 md:min-h-screen md:pb-40 md:pt-16"
              : "py-14 pb-40 md:py-24 md:pb-24 lg:py-32"
          }`}
        >
          {/* Solid black hero base that melts smoothly into the page's scroll background */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, hsl(var(--background)) 0%, hsl(var(--background)) 55%, hsl(var(--background) / 0.92) 70%, hsl(var(--background) / 0.7) 82%, hsl(var(--background) / 0.4) 92%, hsl(var(--background) / 0) 100%)",
            }}
          />
          <XMarkBackground tone="light" opacity={0.05} />



          <div className="relative z-10 w-full max-w-4xl">
            {sub.heroEyebrow && (
              <Reveal as="p" className="mb-5 font-body text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground sm:text-[11px] sm:tracking-[0.32em] md:mb-6">
                {sub.heroEyebrow}
              </Reveal>
            )}
            <Reveal as="h1" className="font-display font-black uppercase leading-[1] tracking-tight text-foreground [font-size:clamp(2.125rem,9vw,2.875rem)] md:text-7xl md:leading-[0.95] lg:text-8xl">
              {sub.heroTitle || mod.name}
            </Reveal>
            <Reveal delay={120} as="p" className="mt-3 font-display font-light tracking-tight text-muted-foreground [font-size:clamp(1.125rem,4.5vw,1.375rem)] md:mt-4 md:text-3xl md:leading-tight lg:text-4xl">
              {sub.heroSubtitle || "by Fanpictor"}
            </Reveal>
            {sub.heroDescriptions ? (
              <Reveal delay={240} className="mt-6 max-w-3xl space-y-4 md:mt-8">
                {sub.heroDescriptions.map((text, i) => (
                  <p key={i} className="font-body text-[15px] leading-[1.6] text-muted-foreground md:text-base">
                    {text}
                  </p>
                ))}
              </Reveal>
            ) : (
              <Reveal delay={240} as="p" className="mt-6 max-w-3xl font-body text-[15px] leading-[1.6] text-muted-foreground md:mt-8 md:text-base">
                {sub.description}
              </Reveal>
            )}
          </div>
        </section>


        {["audience-engagement", "audience-insights", "audience-activation", "configuration-panel"].includes(mod.slug) ? (
          <>
            {mod.slug === "audience-engagement" && sub.slug === "quizzes" ? (
              <QuizzesDemoSection heading={sub.demoHeading} description={sub.demoDescription} />
            ) : mod.slug === "audience-engagement" && sub.slug === "polls" ? (
              <PollsDemoSection heading={sub.demoHeading} description={sub.demoDescription} />
            ) : mod.slug === "audience-insights" && sub.slug === "audience-profile" ? (
              <AudienceProfileSection />
            ) : mod.slug === "audience-insights" && sub.slug === "engagement-behavior" ? (
              <EngagementBehaviorSection />
            ) : mod.slug === "audience-activation" && sub.slug === "branded-engagement" ? (
              <BrandedEngagementSection heading={sub.demoHeading} description={sub.demoDescription} />
            ) : mod.slug === "audience-activation" && sub.slug === "rewards-incentives" ? (
              <AudienceActivationSection variant="rewards" heading={sub.demoHeading} description={sub.demoDescription} />
            ) : mod.slug === "configuration-panel" && sub.slug === "design-configuration" ? (
              <ConfigurationSection variant="design" />
            ) : mod.slug === "configuration-panel" && sub.slug === "content-configuration" ? (
              <ConfigurationSection variant="content" />
            ) : (
              <LightshowDemoSection title={sub.title} heading={sub.demoHeading} description={sub.demoDescription} moduleSlug={mod.slug} submoduleSlug={sub.slug} />
            )}
            {mod.slug === "audience-engagement" && sub.slug === "lightshow" && <EurovisionLightshowSection />}
            {mod.slug === "audience-engagement" && <LightshowQRSection title={sub.title} featureSlug={sub.slug} />}
            {mod.slug === "audience-insights" && <AudienceInsightsCTA />}
          </>
        ) : (
          <>
            {/* Feature Detail Section */}
            <section className="relative overflow-hidden px-5 pb-8 sm:px-8 md:px-16 md:pb-16 lg:px-24 lg:pb-20">
            <XMarkBackground tone="light" opacity={0.05} />

              <div className="relative z-10 w-full max-w-4xl pt-16 md:pt-32 lg:pt-40">
                <h3 className="font-display font-black tracking-tight text-foreground [font-size:clamp(1.875rem,8vw,2.5rem)] leading-[1.05] md:text-5xl lg:text-6xl">
                  Fanpictor {sub.title}
                </h3>
                <p className="mt-5 max-w-3xl font-body text-[15px] leading-[1.6] text-muted-foreground md:mt-6 md:text-base lg:text-lg">
                  {sub.description}
                </p>
              </div>
            </section>


            {/* Video Mockup Section */}
            <VideoMockupSection featureLabel={sub.title} />

            {/* Extra Sections */}
            {sub.extraSections?.map((section, i) => (
              <div key={i}>
                <section className="px-8 pb-8 md:px-16 md:pb-16 lg:px-24 lg:pb-20">
                  <div className="max-w-4xl pt-24 md:pt-32 lg:pt-40">
                    <h3 className="font-display text-3xl font-black tracking-tight text-foreground md:text-5xl lg:text-6xl">
                      {section.heading}
                    </h3>
                    <p className="mt-6 max-w-3xl font-body text-sm leading-relaxed text-muted-foreground md:text-base lg:text-lg">
                      {section.description}
                    </p>
                  </div>
                </section>
                {section.videoLabel && <SimpleVideoSection featureLabel={section.videoLabel} />}
              </div>
            ))}
          </>
        )}

      </main>

      <div className={useScrollBg ? "relative bg-background text-foreground" : ""}>
        <Footer />
      </div>
    </div>
  );
};

export default DemoFeature;
